/**
 * The recruitment (ATS) contract — requisitions, postings, candidates, interviews, offers, and
 * the analytics that roll them up. Internal, authenticated surface only; the unauthenticated
 * public career site and offer-acceptance flow are in careers.contract.ts.
 *
 * Copies employee.contract.ts's conventions exactly (see that file's header for the full
 * rationale): DTOs distinct from the Prisma models, every DTO carrying what `ResourceTarget`
 * needs to authorise it, `paginatedSchema()` for every list.
 *
 * APPROVAL GOES THROUGH `@hrms/workflow`, NEVER A HAND-ROLLED STATUS FLIP (CLAUDE.md invariant 6).
 * `jobRequisitionSchema.status` and `offerLetterSchema.status` mirror whatever the underlying
 * `WorkflowInstance` says once submitted — the route never writes either directly except to copy
 * that value across after calling the engine. See leave.contract.ts's header for why a route that
 * does this makes three separate `withTenant` calls around one workflow operation, not one.
 *
 * MONEY IS A DECIMAL STRING (CLAUDE.md invariant 2) — `annualCtc`/`minSalary`/`maxSalary` use
 * `decimalStringSchema`, always paired with a `currency`, never a bare number.
 *
 * `offer:send`/`offer:rescind` are financial-adjacent mutations (CLAUDE.md invariant 10): a
 * retried send must not double-email a candidate or re-arm the acceptance link, and a retried
 * rescind must not double-fire whatever follows it. Both carry an `Idempotency-Key`.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  assessmentQuestionTypeSchema,
  assessmentStatusSchema,
  candidateSourceSchema,
  candidateStageSchema,
  candidateStatusSchema,
  currencySchema,
  dateOnlySchema,
  decimalStringSchema,
  emailSchema,
  employmentTypeSchema,
  errorBodySchema,
  experienceLevelSchema,
  idempotencyKeySchema,
  instantSchema,
  interviewRecommendationSchema,
  interviewStatusSchema,
  jobPostingStatusSchema,
  meetingProviderSchema,
  offerSourceTypeSchema,
  offerStatusSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  phoneSchema,
  referralBonusStatusSchema,
  requisitionStatusSchema,
  slotOfferStatusSchema,
  uuidSchema,
  workTypeSchema,
} from '../schemas/common.js';
import {
  CANDIDATE_ACTIVITY_KINDS,
  CANDIDATE_EMAIL_KINDS,
  CANDIDATE_EMAIL_STATUSES,
  EMAIL_TIMINGS,
  CANDIDATE_CHECK_KINDS,
  CANDIDATE_CHECK_STATUSES,
  HIRING_TEAM_ROLES,
  KNOCKOUT_ACTIONS,
  POSTING_QUESTION_KINDS,
} from '../enums.js';
import { flowStagesProblem } from '../lib/recruitment-pipeline.js';
import { IMPORT_FIELDS, MAX_IMPORT_ROWS } from '../lib/candidate-import.js';
import { routeMeta } from './meta.js';

/** `?isActive=false` must mean false. `z.coerce.boolean()` turns any non-empty string, including
 *  'false', into true (gap analysis bug 20). */
const queryBooleanSchema = z.enum(['true', 'false']).transform((v) => v === 'true');

const c = initContract();

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

export const jobRequisitionSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  legalEntityId: uuidSchema,
  legalEntityName: z.string(),
  departmentId: uuidSchema,
  departmentName: z.string(),
  locationId: uuidSchema.nullable(),
  locationName: z.string().nullable(),
  title: z.string(),
  headcount: z.number().int().positive(),
  employmentType: employmentTypeSchema,
  workType: workTypeSchema,
  minSalary: decimalStringSchema.nullable(),
  maxSalary: decimalStringSchema.nullable(),
  currency: currencySchema,
  targetHiringDate: dateOnlySchema.nullable(),
  status: requisitionStatusSchema,
  requestedByPersonId: uuidSchema,
  requestedByName: z.string(),
  hiringManagerPersonId: uuidSchema.nullable(),
  hiringManagerName: z.string().nullable(),
  workflowInstanceId: uuidSchema.nullable(),
  openPostingCount: z.number().int().nonnegative(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type JobRequisition = z.infer<typeof jobRequisitionSchema>;

export const jobPostingSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  requisitionId: uuidSchema,
  slug: z.string(),
  title: z.string(),
  description: z.string(),
  requirements: z.string().nullable(),
  departmentName: z.string(),
  locationName: z.string().nullable(),
  workType: workTypeSchema,
  employmentType: employmentTypeSchema,
  minSalary: decimalStringSchema.nullable(),
  maxSalary: decimalStringSchema.nullable(),
  currency: currencySchema,
  isPublic: z.boolean(),
  status: jobPostingStatusSchema,
  candidateCount: z.number().int().nonnegative(),
  /** Also (or only) open to current employees on the internal job board. */
  isInternal: z.boolean(),
  /** The hiring flow this posting's candidates move through. Null: the organisation's default. */
  hiringFlowId: uuidSchema.nullable(),
  hiringFlowName: z.string(),
  createdAt: instantSchema,
});
export type JobPosting = z.infer<typeof jobPostingSchema>;

/**
 * Carries what `ResourceTarget` needs via `departmentId`/`legalEntityId` (from the posting's
 * requisition) — a candidate has no `personId` in the actor sense (see can.ts's `ResourceTarget`
 * doc: today every actor is an employee `Person`, and a candidate is never one).
 */
export const candidateMatchSchema = z.object({
  score: z.number().int().min(0).max(100),
  reasons: z.array(z.string()),
  gaps: z.array(z.string()),
  scoredAt: instantSchema,
  /** `resume` when the resume's text was read, `profile` when only what they typed was. */
  basis: z.enum(['resume', 'profile']),
});
export type CandidateMatch = z.infer<typeof candidateMatchSchema>;

/** A free skills-and-years comparison against the job, with why. Only for ordering; never a verdict. */
export const quickMatchSchema = z.object({
  score: z.number().int().min(0).max(100),
  matched: z.array(z.string()),
  missing: z.array(z.string()),
});
export type QuickMatchDto = z.infer<typeof quickMatchSchema>;

export const RESUME_READ_STATUSES = ['read', 'check', 'failed'] as const;

export const candidateSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  postingId: uuidSchema,
  postingTitle: z.string(),
  departmentId: uuidSchema,
  legalEntityId: uuidSchema,
  firstName: z.string(),
  lastName: z.string(),
  email: emailSchema,
  phone: phoneSchema.nullable(),
  resumeUrl: z.string().url().nullable(),
  linkedinUrl: z.string().url().nullable(),
  /** Self-reported on the public apply form — recruiter context, never auto-scored. */
  coverNote: z.string().nullable(),
  skillTags: z.array(z.string()),
  experienceLevel: experienceLevelSchema.nullable(),
  /** The reference shown back to the candidate on the confirmation screen — see schema.prisma's
   *  `Candidate.applicationRef` doc comment. Null for candidates added before this field existed,
   *  or added internally (createCandidate/referCandidate), which never generate one. */
  applicationRef: z.string().nullable(),
  source: candidateSourceSchema,
  /** The recruitment agency that sent the profile (Hiring setup > Agencies). Optional so other
   *  shapes built from this one stay valid; the candidate routes fill it. */
  agencyId: uuidSchema.nullable().optional(),
  agencyName: z.string().nullable().optional(),
  referredByPersonId: uuidSchema.nullable(),
  referredByName: z.string().nullable(),
  /** The stage TYPE — what analytics, offers and the hand-off run on. */
  stage: candidateStageSchema,
  /** The named stage in the posting's hiring flow, e.g. "Technical round" (an `interview` stage). */
  stageId: uuidSchema.nullable(),
  stageName: z.string(),
  status: candidateStatusSchema,
  rejectionReason: z.string().nullable(),
  rejectionReasonId: uuidSchema.nullable(),
  /** Recruiter's own overall assessment, set directly on the candidate — distinct from
   *  `latestInterviewRating` below, which is a rollup of per-round interviewer scorecards. */
  rating: z.number().int().min(1).max(5).nullable(),
  latestInterviewRating: z.number().int().min(1).max(5).nullable(),
  /** Meaningful only when source = employee_referral. Tracked here; actual payout runs through
   * the existing payroll/expense modules once approved. */
  referralBonusAmount: decimalStringSchema.nullable(),
  referralBonusCurrency: currencySchema.nullable(),
  referralBonusStatus: referralBonusStatusSchema.nullable(),
  /** Years of work read from the resume or given in an import; null when unknown. */
  experienceYears: z.number().nullable(),
  /** How well the resume could be read: `check` means a person should look over the details. */
  resumeReadStatus: z.enum(RESUME_READ_STATUSES).nullable(),
  resumeReadNotes: z.array(z.string()),
  quickMatch: quickMatchSchema.nullable(),
  /** Set when a screening question flagged this application (it is not a rejection). */
  screeningFlag: z.string().nullable(),
  /** An AI estimate of fit against the posting, with its reasons. Advice only: it never moves or
   *  rejects a candidate. */
  aiMatch: candidateMatchSchema.nullable(),
  /** The status of their newest offer (a lapsed one reads `expired`); null before any offer. Lets a
   *  card say "Offer declined" instead of looking like an offer still in progress. */
  latestOfferStatus: offerStatusSchema.nullable(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type Candidate = z.infer<typeof candidateSchema>;

export const interviewScheduleSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  candidateId: uuidSchema,
  candidateName: z.string(),
  candidateStage: candidateStageSchema,
  roundName: z.string(),
  interviewerPersonId: uuidSchema,
  interviewerName: z.string(),
  scheduledAt: instantSchema,
  durationMinutes: z.number().int().positive(),
  meetingUrl: z.string().url().nullable(),
  meetingProvider: meetingProviderSchema,
  status: interviewStatusSchema,
  rating: z.number().int().min(1).max(5).nullable(),
  recommendation: interviewRecommendationSchema.nullable(),
  scorecardNotes: z.string().nullable(),
  /** Everyone interviewing in this round, the lead (`interviewerPersonId`) included. Each files
   *  their own scorecard. */
  panel: z.array(
    z.object({ personId: uuidSchema, name: z.string(), scorecardSubmitted: z.boolean() }),
  ),
  /** The scorecard the panel fills in; null = the simple overall rating only. */
  scorecardTemplateId: uuidSchema.nullable(),
  createdAt: instantSchema,
});
export type InterviewSchedule = z.infer<typeof interviewScheduleSchema>;

// ---- Hiring flows (Keka "Hiring Flow", bug 12) ------------------------------------

export const hiringFlowStageSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  type: candidateStageSchema,
  orderIndex: z.number().int().nonnegative(),
  /** Active candidates in this stage right now. */
  candidateCount: z.number().int().nonnegative(),
});
export type HiringFlowStage = z.infer<typeof hiringFlowStageSchema>;

export const hiringFlowSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  name: z.string(),
  description: z.string().nullable(),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  stages: z.array(hiringFlowStageSchema),
  postingCount: z.number().int().nonnegative(),
  createdAt: instantSchema,
});
export type HiringFlow = z.infer<typeof hiringFlowSchema>;

const flowStageBodySchema = z.object({
  /** Present on an existing stage, so its candidates stay in it when the flow is edited. */
  id: uuidSchema.optional(),
  name: z.string().trim().min(1).max(60),
  type: candidateStageSchema,
});

export const createHiringFlowBodySchema = z
  .object({
    name: z.string().trim().min(1).max(100),
    description: z.string().trim().max(500).nullable().optional(),
    isDefault: z.boolean().default(false),
    stages: z.array(flowStageBodySchema).min(3).max(20),
  })
  .superRefine((body, ctx) => {
    const problem = flowStagesProblem(body.stages);
    if (problem) ctx.addIssue({ code: 'custom', message: problem, path: ['stages'] });
  });

export const updateHiringFlowBodySchema = z
  .object({
    name: z.string().trim().min(1).max(100).optional(),
    description: z.string().trim().max(500).nullable().optional(),
    isDefault: z.boolean().optional(),
    isActive: z.boolean().optional(),
    stages: z.array(flowStageBodySchema).min(3).max(20).optional(),
  })
  .superRefine((body, ctx) => {
    const problem = body.stages ? flowStagesProblem(body.stages) : null;
    if (problem) ctx.addIssue({ code: 'custom', message: problem, path: ['stages'] });
  });

/** The pipeline's columns and how many active candidates are in each. With a posting: that posting's
 *  flow. Without: the seven stage types, since postings can use different flows. */
export const pipelineSummarySchema = z.object({
  flowId: uuidSchema.nullable(),
  flowName: z.string(),
  stages: z.array(
    z.object({
      /** Null on the all-postings view, where columns are the stage types. */
      id: uuidSchema.nullable(),
      name: z.string(),
      type: candidateStageSchema,
      count: z.number().int().nonnegative(),
    }),
  ),
});
export type PipelineSummary = z.infer<typeof pipelineSummarySchema>;

export const pipelineSummaryQuerySchema = z.object({ postingId: uuidSchema.optional() });

// ---- Rejection reasons ------------------------------------------------------------

export const rejectionReasonSchema = z.object({
  id: uuidSchema,
  label: z.string(),
  isActive: z.boolean(),
  orderIndex: z.number().int().nonnegative(),
});
export type RejectionReason = z.infer<typeof rejectionReasonSchema>;

export const createRejectionReasonBodySchema = z.object({ label: z.string().trim().min(1).max(100) });
export const updateRejectionReasonBodySchema = z.object({
  label: z.string().trim().min(1).max(100).optional(),
  isActive: z.boolean().optional(),
});

// ---- Moving, rejecting, reconsidering -----------------------------------------------

const emailTimingSchema = z.enum(EMAIL_TIMINGS);

const rejectFields = {
  /** A reason from the organisation's list, and/or a note; one of the two is required. */
  reasonId: uuidSchema.optional(),
  note: z.string().trim().max(500).optional(),
  /** Send the candidate the organisation's rejection email (or the template named). */
  sendEmail: z.boolean().default(false),
  emailTiming: emailTimingSchema.default('now'),
  emailTemplateId: uuidSchema.optional(),
};
const needsReason = (b: { reasonId?: string; note?: string }) => !!b.reasonId || !!b.note;
const needsReasonMessage = { message: 'Give a rejection reason', path: ['reasonId'] };

export const rejectCandidateBodySchema = z.object(rejectFields).refine(needsReason, needsReasonMessage);

export const reconsiderCandidateBodySchema = z.object({
  /** Where the candidate re-enters the flow; default: the first stage after Applied. */
  stageId: uuidSchema.optional(),
  note: z.string().trim().max(500).optional(),
});

const bulkIdsSchema = z.array(uuidSchema).min(1).max(100);

export const bulkMoveCandidatesBodySchema = z
  .object({
    candidateIds: bulkIdsSchema,
    stageId: uuidSchema.optional(),
    stage: candidateStageSchema.optional(),
  })
  .refine((b) => (b.stageId !== undefined) !== (b.stage !== undefined), {
    message: 'Give a stageId or a stage',
    path: ['stageId'],
  });

export const bulkRejectCandidatesBodySchema = z
  .object({ candidateIds: bulkIdsSchema, ...rejectFields })
  .refine(needsReason, needsReasonMessage);

/** One line per candidate: a bulk action never half-fails silently. */
export const bulkResultSchema = z.object({
  results: z.array(
    z.object({ candidateId: uuidSchema, ok: z.boolean(), error: z.string().nullable() }),
  ),
});
export type BulkResult = z.infer<typeof bulkResultSchema>;

// ---- Timeline, notes, email -----------------------------------------------------------

export const candidateActivitySchema = z.object({
  id: uuidSchema,
  kind: z.enum(CANDIDATE_ACTIVITY_KINDS),
  summary: z.string(),
  /** Who did it; null for the candidate themselves or the system. */
  actorName: z.string().nullable(),
  /** The facts behind the summary (reason, from/to stage, ids), for the detail view. */
  data: z.record(z.string(), z.unknown()),
  createdAt: instantSchema,
});
export type CandidateActivity = z.infer<typeof candidateActivitySchema>;

export const candidateNoteSchema = z.object({
  id: uuidSchema,
  candidateId: uuidSchema,
  body: z.string(),
  authorPersonId: uuidSchema,
  authorName: z.string(),
  mentions: z.array(z.object({ personId: uuidSchema, name: z.string() })),
  createdAt: instantSchema,
});
export type CandidateNote = z.infer<typeof candidateNoteSchema>;

export const addCandidateNoteBodySchema = z.object({
  body: z.string().trim().min(1).max(4000),
  /** People tagged with @; only the requisition's hiring team can be mentioned. */
  mentionPersonIds: z.array(uuidSchema).max(20).default([]),
});

export const candidateEmailSchema = z.object({
  id: uuidSchema,
  candidateId: uuidSchema,
  kind: z.enum(CANDIDATE_EMAIL_KINDS),
  subject: z.string(),
  body: z.string(),
  status: z.enum(CANDIDATE_EMAIL_STATUSES),
  /** When a `scheduled` email goes out; for a sent one, when it was queued. */
  sendAt: instantSchema,
  sentAt: instantSchema.nullable(),
  failureReason: z.string().nullable(),
  sentByName: z.string().nullable(),
  createdAt: instantSchema,
});
export type CandidateEmail = z.infer<typeof candidateEmailSchema>;

export const sendCandidateEmailBodySchema = z.object({
  candidateIds: bulkIdsSchema,
  subject: z.string().trim().min(1).max(200),
  body: z.string().trim().min(1).max(10_000),
  timing: emailTimingSchema.default('now'),
});

export const sendCandidateEmailResultSchema = z.object({
  results: z.array(
    z.object({
      candidateId: uuidSchema,
      ok: z.boolean(),
      status: z.enum(CANDIDATE_EMAIL_STATUSES).nullable(),
      error: z.string().nullable(),
    }),
  ),
});

export const previewCandidateEmailBodySchema = z.object({
  candidateId: uuidSchema,
  subject: z.string().trim().max(200),
  body: z.string().trim().max(10_000),
});
export const candidateEmailPreviewSchema = z.object({
  subject: z.string(),
  body: z.string(),
  /** `{{tokens}}` we don't fill in — they would be sent as typed. */
  unknownTokens: z.array(z.string()),
});

export const candidateEmailTemplateSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  kind: z.enum(CANDIDATE_EMAIL_KINDS),
  subject: z.string(),
  body: z.string(),
  /** The one used when this kind is sent without naming a template. */
  isDefault: z.boolean(),
  isActive: z.boolean(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type CandidateEmailTemplate = z.infer<typeof candidateEmailTemplateSchema>;

export const createCandidateEmailTemplateBodySchema = z.object({
  name: z.string().trim().min(1).max(100),
  kind: z.enum(CANDIDATE_EMAIL_KINDS).default('message'),
  subject: z.string().trim().min(1).max(200),
  body: z.string().trim().min(1).max(10_000),
  isDefault: z.boolean().default(false),
});
export const updateCandidateEmailTemplateBodySchema = z.object({
  name: z.string().trim().min(1).max(100).optional(),
  subject: z.string().trim().min(1).max(200).optional(),
  body: z.string().trim().min(1).max(10_000).optional(),
  isDefault: z.boolean().optional(),
  isActive: z.boolean().optional(),
});

// ---- Hiring team (bug 11) ---------------------------------------------------

export const hiringTeamRoleSchema = z.enum(HIRING_TEAM_ROLES);

export const requisitionMemberSchema = z.object({
  personId: uuidSchema,
  name: z.string(),
  role: hiringTeamRoleSchema,
});
export type RequisitionMember = z.infer<typeof requisitionMemberSchema>;

export const setRequisitionMembersBodySchema = z.object({
  members: z
    .array(z.object({ personId: uuidSchema, role: hiringTeamRoleSchema }))
    .max(50)
    .refine((m) => new Set(m.map((x) => x.personId)).size === m.length, {
      message: 'Each person can have one role on the hiring team',
    }),
});

// ---- Scorecard templates (bug 13, Keka scorecards, Greenhouse interview kits) --

export const scorecardCriterionSchema = z.object({
  id: z.string().trim().min(1).max(64),
  label: z.string().trim().min(1).max(200),
  /** What a good answer looks like — shown to the interviewer next to the rating. */
  guidance: z.string().trim().max(1000).optional(),
});

export const scorecardSectionSchema = z.object({
  id: z.string().trim().min(1).max(64),
  title: z.string().trim().min(1).max(120),
  criteria: z.array(scorecardCriterionSchema).min(1).max(20),
});

const ratingScaleSchema = z.union([z.literal(3), z.literal(5)]);

export const scorecardTemplateSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  name: z.string(),
  description: z.string().nullable(),
  ratingScale: ratingScaleSchema,
  /** One label per point, lowest first (e.g. Poor … Excellent). */
  ratingLabels: z.array(z.string()),
  sections: z.array(scorecardSectionSchema),
  /** The interview kit: questions the interviewer is suggested to ask. */
  interviewQuestions: z.array(z.string()),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type ScorecardTemplate = z.infer<typeof scorecardTemplateSchema>;

const scorecardTemplateFields = {
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(1000).nullable().optional(),
  ratingScale: ratingScaleSchema.default(5),
  ratingLabels: z.array(z.string().trim().min(1).max(40)).min(3).max(5),
  sections: z.array(scorecardSectionSchema).min(1).max(10),
  interviewQuestions: z.array(z.string().trim().min(1).max(500)).max(30).default([]),
  isDefault: z.boolean().default(false),
};

const labelsMatchScale = (t: { ratingScale?: number; ratingLabels?: string[] }) =>
  t.ratingLabels === undefined || t.ratingLabels.length === (t.ratingScale ?? 5);

export const createScorecardTemplateBodySchema = z
  .object(scorecardTemplateFields)
  .refine(labelsMatchScale, { message: 'Give one label per point on the scale', path: ['ratingLabels'] });

export const updateScorecardTemplateBodySchema = z
  .object({
    name: scorecardTemplateFields.name.optional(),
    description: scorecardTemplateFields.description,
    ratingScale: ratingScaleSchema.optional(),
    ratingLabels: scorecardTemplateFields.ratingLabels.optional(),
    sections: scorecardTemplateFields.sections.optional(),
    interviewQuestions: z.array(z.string().trim().min(1).max(500)).max(30).optional(),
    isDefault: z.boolean().optional(),
    isActive: z.boolean().optional(),
  })
  .refine(
    (t) => (t.ratingScale === undefined) === (t.ratingLabels === undefined),
    { message: 'Change the scale and its labels together', path: ['ratingLabels'] },
  )
  .refine(labelsMatchScale, { message: 'Give one label per point on the scale', path: ['ratingLabels'] });

export const listScorecardTemplatesQuerySchema = paginationQuerySchema.extend({
  isActive: z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
});

/** One interviewer's filed scorecard. */
export const interviewScorecardSchema = z.object({
  id: uuidSchema,
  interviewId: uuidSchema,
  roundName: z.string(),
  interviewerPersonId: uuidSchema,
  interviewerName: z.string(),
  /** Overall rating, 1..scale. */
  rating: z.number().int().min(1).max(5),
  ratingScale: ratingScaleSchema,
  recommendation: interviewRecommendationSchema,
  notes: z.string().nullable(),
  ratings: z.array(
    z.object({
      criterionId: z.string(),
      criterionLabel: z.string(),
      sectionTitle: z.string(),
      rating: z.number().int().min(1).max(5),
      note: z.string().nullable(),
    }),
  ),
  submittedAt: instantSchema,
});
export type InterviewScorecard = z.infer<typeof interviewScorecardSchema>;

/**
 * Every scorecard for a candidate (or one interview), with averages. `hidden` is true when the
 * caller is on a panel that hasn't seen the others yet: their own scorecard comes first, so nobody
 * is anchored by a colleague's score (bug 13). Recruiters see everything.
 */
export const scorecardSummarySchema = z.object({
  hidden: z.boolean(),
  scorecards: z.array(interviewScorecardSchema),
  averages: z.object({
    /** Overall ratings normalised to a 5-point scale, so 3- and 5-point scorecards combine. */
    overall: z.number().nullable(),
    byRound: z.array(z.object({ roundName: z.string(), average: z.number() })),
    byInterviewer: z.array(
      z.object({ personId: uuidSchema, name: z.string(), average: z.number() }),
    ),
    bySection: z.array(z.object({ sectionTitle: z.string(), average: z.number() })),
  }),
  recommendations: z.record(interviewRecommendationSchema, z.number().int()),
});
export type ScorecardSummary = z.infer<typeof scorecardSummarySchema>;

// ---- My interviews (an interviewer's own view) ---------------------------------

export const myInterviewSchema = z.object({
  interviewId: uuidSchema,
  candidateId: uuidSchema,
  candidateName: z.string(),
  postingTitle: z.string(),
  roundName: z.string(),
  scheduledAt: instantSchema,
  durationMinutes: z.number().int().positive(),
  meetingUrl: z.string().url().nullable(),
  status: interviewStatusSchema,
  isLead: z.boolean(),
  scorecardSubmitted: z.boolean(),
});
export type MyInterview = z.infer<typeof myInterviewSchema>;

export const myInterviewDetailSchema = myInterviewSchema.extend({
  candidate: z.object({
    email: emailSchema,
    phone: phoneSchema.nullable(),
    linkedinUrl: z.string().url().nullable(),
    skillTags: z.array(z.string()),
    experienceLevel: experienceLevelSchema.nullable(),
    coverNote: z.string().nullable(),
    /** True when the resume was uploaded here (download it through downloadMyInterviewResume). */
    hasResumeFile: z.boolean(),
    /** A pasted resume link (Drive, LinkedIn), opened directly. */
    resumeLink: z.string().url().nullable(),
  }),
  panel: z.array(z.object({ personId: uuidSchema, name: z.string() })),
  template: scorecardTemplateSchema.nullable(),
  myScorecard: interviewScorecardSchema.nullable(),
});
export type MyInterviewDetail = z.infer<typeof myInterviewDetailSchema>;

export const listMyInterviewsQuerySchema = paginationQuerySchema.extend({
  when: z.enum(['upcoming', 'past', 'all']).default('upcoming'),
});

/** The salary structure behind an offer's CTC (Keka: "CTC breakup from the salary structure"),
 *  frozen when the offer is made so what the candidate signed never shifts under them. */
export const ctcBreakupSchema = z.object({
  templateName: z.string(),
  annualCtc: decimalStringSchema,
  currency: currencySchema,
  monthlyGross: decimalStringSchema,
  annualGross: decimalStringSchema,
  lines: z.array(
    z.object({
      componentName: z.string(),
      componentType: z.string(),
      calculationLabel: z.string(),
      monthly: decimalStringSchema,
      annual: decimalStringSchema,
    }),
  ),
});
export type CtcBreakup = z.infer<typeof ctcBreakupSchema>;

export const offerLetterSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  candidateId: uuidSchema,
  /** From the posting's requisition — the scope every permission on this offer is checked in. */
  legalEntityId: uuidSchema,
  departmentId: uuidSchema,
  candidateName: z.string(),
  candidateEmail: emailSchema,
  postingTitle: z.string(),
  /** Recruiter-editable, defaults to `postingTitle` at creation — see schema.prisma's doc comment
   *  for why it's independent of the posting afterward. */
  designation: z.string().nullable(),
  /** Null when withheld — see `annualCtcVisible`. */
  annualCtc: decimalStringSchema.nullable(),
  currency: currencySchema,
  targetJoinDate: dateOnlySchema,
  status: offerStatusSchema,
  workflowInstanceId: uuidSchema.nullable(),
  sourceType: offerSourceTypeSchema,
  /** Snapshot taken when the template was applied — editing the template afterward never rewords
   * an offer someone already received. */
  templateId: uuidSchema.nullable(),
  renderedBody: z.string().nullable(),
  /** Set only when `sourceType: 'upload'`. Not a link to open: the file is private, so the web
   *  app fetches it through `downloadOfferDocument`. */
  documentUrl: z.string().url().nullable(),
  /** The login (work) email the hire gets on acceptance. Null: the candidate's own email is used
   *  until HR assigns a work address. */
  workEmail: emailSchema.nullable(),
  /** The CTC template (salary structure) the accepted offer's Compensation is built from. Null:
   *  the annual CTC is recorded alone and payroll completes the structure. */
  ctcTemplateId: uuidSchema.nullable(),
  /** The pre-joining employment this offer created once accepted. */
  employmentId: uuidSchema.nullable(),
  /** 1 for the first offer; a revision is the next version and replaces the one it came from. */
  version: z.number().int().positive(),
  supersedesOfferId: uuidSchema.nullable(),
  /** Null when withheld (see `annualCtcVisible`) or when the offer names no salary structure. */
  ctcBreakup: ctcBreakupSchema.nullable(),
  /** Whose signature the letter carries (the company's authorised signatory), if any. */
  signatoryName: z.string().nullable(),
  /** The candidate's e-signature, once they have accepted: the name they typed and when. */
  signedName: z.string().nullable(),
  signedAt: instantSchema.nullable(),
  /** False when the caller may not see offer salaries (`offer.compensation:read`): the offer is
   *  still listed, only `annualCtc` is withheld (null). */
  annualCtcVisible: z.boolean(),
  /** True when the CTC is outside the requisition's salary band; `bandExceptionReason` says why. */
  outsideBand: z.boolean(),
  bandExceptionReason: z.string().nullable(),
  /** When a sent offer stops accepting a response. Set on send; null before that. */
  expiresAt: instantSchema.nullable(),
  sentAt: instantSchema.nullable(),
  acceptedAt: instantSchema.nullable(),
  createdAt: instantSchema,
});
export type OfferLetter = z.infer<typeof offerLetterSchema>;

/** A private recruitment file (an uploaded resume or offer PDF), returned inline because it is
 *  never served from a public URL. */
export const recruitmentFileSchema = z.object({
  fileName: z.string(),
  mimeType: z.string(),
  contentBase64: z.string(),
});
export type RecruitmentFile = z.infer<typeof recruitmentFileSchema>;

export const offerTemplateSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  name: z.string(),
  /** {{CANDIDATE_NAME}}, {{DESIGNATION}}, {{JOIN_DATE}}, {{ANNUAL_CTC}}, {{ORG_NAME}}, plus any
   * arbitrary {{CUSTOM}} token the author adds — dumb {{var}} substitution only, same posture as
   * @hrms/notify's interpolate(): no logic, no eval. A custom token's value is supplied per-offer
   * at createOffer time (`createOfferBodySchema.customVariables`), not stored on the template. */
  body: z.string(),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  /** Branding images shown alongside the rendered body — cosmetic, never substituted into `body`. */
  letterheadUrls: z.array(z.string().url()),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type OfferTemplate = z.infer<typeof offerTemplateSchema>;

/**
 * A recruiter-proposed set of times a candidate can self-book — NOT computed from anyone's real
 * calendar free/busy (that needs domain-wide delegation only the org's own Workspace/365 admin can
 * grant; see CalendarIntegration's schema comment). `slots` is recruiter-entered.
 */
export const interviewSlotOfferSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  candidateId: uuidSchema,
  candidateName: z.string(),
  roundName: z.string(),
  interviewerPersonId: uuidSchema,
  interviewerName: z.string(),
  durationMinutes: z.number().int().positive(),
  slots: z.array(instantSchema),
  shareUrl: z.string().url(),
  status: slotOfferStatusSchema,
  bookedSlotIndex: z.number().int().nullable(),
  createdAt: instantSchema,
  expiresAt: instantSchema.nullable(),
});
export type InterviewSlotOffer = z.infer<typeof interviewSlotOfferSchema>;

/**
 * One question in an `AssessmentTemplate`. Dumb data, no branching logic — same posture as
 * `@hrms/notify`'s `{{var}}` interpolation and `offerTemplateSchema.body`'s `{{TOKEN}}`s: `options`
 * only means anything when `type: 'multiple_choice'`, and nothing here evaluates or scores an
 * answer server-side beyond storing what the candidate submitted.
 */
export const assessmentQuestionSchema = z.object({
  id: z.string().trim().min(1).max(64),
  text: z.string().trim().min(1).max(500),
  type: assessmentQuestionTypeSchema,
  options: z.array(z.string().trim().min(1).max(200)).min(2).max(10).optional(),
});
export type AssessmentQuestion = z.infer<typeof assessmentQuestionSchema>;

/** The question-builder template — reusable, sent to any number of candidates. */
export const assessmentTemplateSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  name: z.string(),
  description: z.string().nullable(),
  questions: z.array(assessmentQuestionSchema),
  isActive: z.boolean(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type AssessmentTemplate = z.infer<typeof assessmentTemplateSchema>;

export const assessmentAnswerSchema = z.object({
  questionId: z.string(),
  /** Always a string — free text, the chosen option's own label, or a rating rendered as its
   *  digit. See CandidateAssessment.answers's schema.prisma comment for why. */
  value: z.string().trim().max(4000),
});
export type AssessmentAnswer = z.infer<typeof assessmentAnswerSchema>;

/**
 * One template sent to one candidate. `questions`/`templateName` are a snapshot taken at send
 * time (see schema.prisma's `CandidateAssessment` doc comment) — editing the template afterward
 * never changes what this candidate holds a link to answer.
 */
export const candidateAssessmentSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  candidateId: uuidSchema,
  candidateName: z.string(),
  templateId: uuidSchema,
  templateName: z.string(),
  questions: z.array(assessmentQuestionSchema),
  status: assessmentStatusSchema,
  answers: z.array(assessmentAnswerSchema).nullable(),
  sentAt: instantSchema,
  submittedAt: instantSchema.nullable(),
  expiresAt: instantSchema.nullable(),
});
export type CandidateAssessment = z.infer<typeof candidateAssessmentSchema>;

/**
 * A candidate a recruiter wants to keep track of for future roles, independent of any one
 * posting's pipeline (see schema.prisma's `TalentPoolEntry` doc comment). `postingTitle` is the
 * posting they originally applied through — shown for context, not because the pool is scoped to
 * it.
 */
export const talentPoolEntrySchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  candidateId: uuidSchema,
  candidateName: z.string(),
  candidateEmail: emailSchema,
  resumeUrl: z.string().url().nullable(),
  postingTitle: z.string(),
  skillTags: z.array(z.string()),
  reason: z.string().nullable(),
  addedByPersonId: uuidSchema,
  addedByName: z.string(),
  addedAt: instantSchema,
});
export type TalentPoolEntry = z.infer<typeof talentPoolEntrySchema>;

// ---------------------------------------------------------------------------
// Screening questions, careers page settings, AI (recruitment R6)
// ---------------------------------------------------------------------------

/** How an answer is judged. `yes_no` / `choice`: the answers that pass. `number`: inclusive bounds. */
export const knockoutRuleSchema = z.object({
  action: z.enum(KNOCKOUT_ACTIONS),
  accept: z.array(z.string().trim().min(1).max(200)).max(20).optional(),
  min: decimalStringSchema.optional(),
  max: decimalStringSchema.optional(),
});
export type KnockoutRule = z.infer<typeof knockoutRuleSchema>;

const postingQuestionFields = {
  label: z.string().trim().min(3).max(300),
  helpText: z.string().trim().max(300).nullable().optional(),
  kind: z.enum(POSTING_QUESTION_KINDS),
  required: z.boolean(),
  /** The options of a `choice` question; empty for every other kind. */
  options: z.array(z.string().trim().min(1).max(200)).max(20).default([]),
  knockout: knockoutRuleSchema.nullable().optional(),
};

/** The one place a question's rules are checked, shared by the setter and tests. */
export function postingQuestionProblem(q: {
  kind: (typeof POSTING_QUESTION_KINDS)[number];
  options: readonly string[];
  knockout?: KnockoutRule | null;
}): string | null {
  if (q.kind === 'choice' && q.options.length < 2) return 'A choice question needs at least two options';
  if (q.kind !== 'choice' && q.options.length > 0) return 'Only a choice question has options';
  const rule = q.knockout;
  if (!rule) return null;
  if (q.kind === 'text' || q.kind === 'long_text') return 'A free-text question cannot be a knockout';
  if (q.kind === 'yes_no') {
    if (!rule.accept || rule.accept.length !== 1 || !['yes', 'no'].includes(rule.accept[0]!)) {
      return 'A yes/no knockout names the one answer that passes: yes or no';
    }
  }
  if (q.kind === 'choice') {
    if (!rule.accept || rule.accept.length === 0) return 'A choice knockout names the options that pass';
    if (rule.accept.some((a) => !q.options.includes(a))) return 'A passing option is not one of the options';
  }
  if (q.kind === 'number') {
    if (rule.min === undefined && rule.max === undefined) return 'A number knockout needs a minimum or a maximum';
    if (rule.min !== undefined && rule.max !== undefined && Number(rule.min) > Number(rule.max)) {
      return 'The minimum is above the maximum';
    }
  }
  return null;
}

export const postingQuestionSchema = z.object({
  id: uuidSchema,
  ...postingQuestionFields,
  helpText: z.string().nullable(),
  knockout: knockoutRuleSchema.nullable(),
});
export type PostingQuestion = z.infer<typeof postingQuestionSchema>;

export const postingQuestionInputSchema = z.object(postingQuestionFields).superRefine((q, ctx) => {
  const problem = postingQuestionProblem({ kind: q.kind, options: q.options, knockout: q.knockout });
  if (problem) ctx.addIssue({ code: 'custom', message: problem, path: ['kind'] });
});

/** Replaces the posting's whole question list, in the order given. */
export const setPostingQuestionsBodySchema = z.object({
  questions: z.array(postingQuestionInputSchema).max(20),
});

export const candidateAnswerSchema = z.object({
  id: uuidSchema,
  /** What was asked, as it read when the candidate answered. */
  label: z.string(),
  kind: z.enum(POSTING_QUESTION_KINDS),
  answer: z.string(),
  /** False when this answer broke a knockout rule. */
  passed: z.boolean(),
});
export type CandidateAnswer = z.infer<typeof candidateAnswerSchema>;

export const DEFAULT_PRIVACY_NOTICE =
  'We collect the details you give us, such as your name, contact details, resume and answers, to ' +
  'consider you for this and other roles. Only our hiring team can see them. We keep them for the ' +
  'retention period below after your last activity and then delete them, and you can ask us to ' +
  'delete them sooner by writing to the recruiting team.';

export const careersSettingsSchema = z.object({
  headline: z.string().nullable(),
  about: z.string().nullable(),
  logoUrl: z.string().url().nullable(),
  bannerUrl: z.string().url().nullable(),
  /** A CSS colour for buttons and links on the careers pages, e.g. "#0b7a75". */
  accentColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable(),
  privacyNotice: z.string(),
  /** Goes up every time the notice text changes; the version a candidate agreed to is recorded. */
  privacyVersion: z.number().int().positive(),
  /** Months after a candidate's last activity before their record is deleted. */
  retentionMonths: z.number().int().min(1).max(120),
});
export type CareersSettings = z.infer<typeof careersSettingsSchema>;

export const updateCareersSettingsBodySchema = z.object({
  headline: z.string().trim().max(200).nullable().optional(),
  about: z.string().trim().max(4000).nullable().optional(),
  logoUrl: z.string().url().nullable().optional(),
  bannerUrl: z.string().url().nullable().optional(),
  accentColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Use a six-digit hex colour like #0b7a75').nullable().optional(),
  privacyNotice: z.string().trim().min(40).max(6000).optional(),
  retentionMonths: z.number().int().min(1).max(120).optional(),
});

export const generateJobDescriptionBodySchema = z.object({
  title: z.string().trim().min(2).max(200),
  level: experienceLevelSchema.optional(),
  skills: z.array(z.string().trim().min(1).max(60)).max(20).default([]),
  department: z.string().trim().max(120).optional(),
  workType: workTypeSchema.optional(),
  /** Anything else the model should know: team, product, must-haves. */
  notes: z.string().trim().max(1000).optional(),
});

export const generatedJobDescriptionSchema = z.object({
  description: z.string(),
  requirements: z.string(),
  /** Anything a recruiter should know before using the text, e.g. it was shortened. */
  warnings: z.array(z.string()),
});
export type GeneratedJobDescription = z.infer<typeof generatedJobDescriptionSchema>;


export const scorecardTextSummarySchema = z.object({
  summary: z.string(),
  strengths: z.array(z.string()),
  concerns: z.array(z.string()),
  /** How many scorecards it read, so nobody mistakes one opinion for a consensus. */
  basedOn: z.number().int().nonnegative(),
});
export type ScorecardSummaryText = z.infer<typeof scorecardTextSummarySchema>;

/** The result for each file of a bulk resume upload. */
export const bulkResumeFileResultSchema = z.object({
  fileName: z.string(),
  status: z.enum(['created', 'duplicate', 'failed']),
  reason: z.string().optional(),
  candidateId: z.string().optional(),
  candidateName: z.string().optional(),
  readStatus: z.enum(RESUME_READ_STATUSES).optional(),
  /** AI was used because the free reading was weak. */
  usedAi: z.boolean(),
  quickMatch: z.number().int().min(0).max(100).nullable().optional(),
});
export type BulkResumeFileResult = z.infer<typeof bulkResumeFileResultSchema>;
export const bulkResumeResultSchema = z.object({ files: z.array(bulkResumeFileResultSchema) });
export type BulkResumeResult = z.infer<typeof bulkResumeResultSchema>;

export const scoreTopBodySchema = z.object({
  /** How many of the best-matching candidates to estimate with AI (the group). */
  topN: z.number().int().min(1).max(200).default(100),
  /** How many to score in this call (each takes a few seconds); call again for the rest. */
  batch: z.number().int().min(1).max(10).default(5),
});
export const scoreTopResultSchema = z.object({
  scored: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  /** Still to do in the group. */
  remaining: z.number().int().nonnegative(),
  stoppedBecause: z.enum(['ai_unavailable']).nullable(),
});

export const eraseCandidateBodySchema = z.object({
  /** Why, for the record (a data-deletion request, a retention clean-up). Never shown to anyone else. */
  reason: z.string().trim().min(3).max(300),
});

// ---------------------------------------------------------------------------
// Profiles across jobs, background checks, joining, agencies, saved searches (recruitment R7)
// ---------------------------------------------------------------------------

/** Another application by the same person: linked to this profile, or merely sharing an email or phone. */
export const candidateApplicationSchema = z.object({
  id: uuidSchema,
  postingTitle: z.string(),
  stage: candidateStageSchema,
  stageName: z.string(),
  status: candidateStatusSchema,
  appliedAt: instantSchema,
  /** True once it shares this candidate's profile; false means "looks like the same person". */
  linked: z.boolean(),
  matchedBy: z.enum(['profile', 'email', 'phone']),
});
export type CandidateApplication = z.infer<typeof candidateApplicationSchema>;

export const linkCandidateBodySchema = z.object({ otherCandidateId: uuidSchema });

export const candidateCheckSchema = z.object({
  id: uuidSchema,
  kind: z.enum(CANDIDATE_CHECK_KINDS),
  status: z.enum(CANDIDATE_CHECK_STATUSES),
  /** Who is doing it: a verification vendor, an HR executive, a referee's company. */
  provider: z.string().nullable(),
  notes: z.string().nullable(),
  requestedAt: instantSchema,
  completedAt: instantSchema.nullable(),
});
export type CandidateCheck = z.infer<typeof candidateCheckSchema>;

export const createCandidateCheckBodySchema = z.object({
  kind: z.enum(CANDIDATE_CHECK_KINDS),
  provider: z.string().trim().max(120).optional(),
  notes: z.string().trim().max(2000).optional(),
});
export const updateCandidateCheckBodySchema = z.object({
  status: z.enum(CANDIDATE_CHECK_STATUSES).optional(),
  provider: z.string().trim().max(120).nullable().optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
});

export const JOIN_STATUSES = ['upcoming', 'due_today', 'overdue', 'joined'] as const;
export const joiningEntrySchema = z.object({
  offerId: uuidSchema,
  candidateId: uuidSchema,
  candidateName: z.string(),
  designation: z.string().nullable(),
  postingTitle: z.string(),
  targetJoinDate: dateOnlySchema,
  joinStatus: z.enum(JOIN_STATUSES),
  /** Days until the join date; negative once it has passed. */
  daysToJoin: z.number().int(),
  employmentId: uuidSchema.nullable(),
  checks: z.object({
    pending: z.number().int().nonnegative(),
    clear: z.number().int().nonnegative(),
    issue: z.number().int().nonnegative(),
  }),
});
export type JoiningEntry = z.infer<typeof joiningEntrySchema>;

export const didNotJoinBodySchema = z.object({ reason: z.string().trim().min(3).max(300) });

export const recruitmentAgencySchema = z.object({
  id: uuidSchema,
  name: z.string(),
  contactName: z.string().nullable(),
  contactEmail: emailSchema,
  /** The agency's fee as a percentage of the annual CTC of a hire it made; null = no fee agreed. */
  feePercent: decimalStringSchema.nullable(),
  isActive: z.boolean(),
  submissions: z.number().int().nonnegative(),
  hired: z.number().int().nonnegative(),
  createdAt: instantSchema,
});
export type RecruitmentAgency = z.infer<typeof recruitmentAgencySchema>;

export const createAgencyBodySchema = z.object({
  name: z.string().trim().min(2).max(120),
  contactName: z.string().trim().max(120).optional(),
  contactEmail: emailSchema,
  feePercent: decimalStringSchema.optional(),
});
export const updateAgencyBodySchema = z.object({
  name: z.string().trim().min(2).max(120).optional(),
  contactName: z.string().trim().max(120).nullable().optional(),
  contactEmail: emailSchema.optional(),
  feePercent: decimalStringSchema.nullable().optional(),
  isActive: z.boolean().optional(),
});
/** The portal token is shown once, when it is created or replaced; only its hash is stored. */
export const agencyWithTokenSchema = z.object({ agency: recruitmentAgencySchema, portalToken: z.string() });

export const talentPoolSearchSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  skills: z.array(z.string()),
  text: z.string().nullable(),
  createdByName: z.string(),
  createdAt: instantSchema,
});
export type TalentPoolSearch = z.infer<typeof talentPoolSearchSchema>;
export const createTalentPoolSearchBodySchema = z.object({
  name: z.string().trim().min(2).max(100),
  skills: z.array(z.string().trim().min(1).max(100)).max(20).default([]),
  text: z.string().trim().max(200).optional(),
});

// ---------------------------------------------------------------------------
// Importing candidates from a spreadsheet
// ---------------------------------------------------------------------------

/** Where an imported candidate came from; the sources a recruiter can truthfully pick for a file. */
export const IMPORT_SOURCES = ['naukri', 'linkedin', 'indeed', 'social', 'direct', 'other'] as const;

/** One row after the columns were matched: raw text, all optional, none trusted (the server re-cleans it). */
export const importRowSchema = z.object(
  Object.fromEntries(IMPORT_FIELDS.map((f) => [f, z.string().max(2000).optional()])) as Record<
    (typeof IMPORT_FIELDS)[number],
    z.ZodOptional<z.ZodString>
  >,
);

export const parseCandidateSpreadsheetBodySchema = z.object({
  fileName: z.string().max(255),
  /** An .xlsx file, base64. CSV is read in the browser and never comes here. */
  contentBase64: z.string().max(8_000_000),
});

export const parsedSpreadsheetSchema = z.object({
  headers: z.array(z.string()),
  rows: z.array(z.array(z.string())),
  /** More rows than one import takes: only the first were read. */
  truncated: z.boolean(),
});

export const previewCandidateImportBodySchema = z.object({
  rows: z.array(importRowSchema).min(1).max(MAX_IMPORT_ROWS),
});

export const importRowStatusSchema = z.enum(['new', 'duplicate_here', 'duplicate_in_file', 'linked_elsewhere', 'invalid']);

export const candidateImportPreviewSchema = z.object({
  rows: z.array(
    z.object({
      index: z.number().int().nonnegative(),
      status: importRowStatusSchema,
      reason: z.string().optional(),
      /** Something was dropped from an otherwise good row (a phone that was not a number). */
      warnings: z.array(z.string()),
    }),
  ),
  summary: z.object({
    new: z.number().int().nonnegative(),
    duplicateHere: z.number().int().nonnegative(),
    duplicateInFile: z.number().int().nonnegative(),
    linkedElsewhere: z.number().int().nonnegative(),
    invalid: z.number().int().nonnegative(),
  }),
});
export type CandidateImportPreview = z.infer<typeof candidateImportPreviewSchema>;

export const importCandidatesBodySchema = z.object({
  rows: z.array(importRowSchema).min(1).max(MAX_IMPORT_ROWS),
  source: z.enum(IMPORT_SOURCES),
  /** A duplicate on this job is skipped; `fill_blanks` adds what the existing record is missing, never overwriting. */
  onDuplicate: z.enum(['skip', 'fill_blanks']).default('skip'),
  /** The recruiter confirms these people applied through the source named, and their data may be held. */
  attested: z.literal(true, { message: 'Confirm where these candidates came from' }),
});

export const candidateImportResultSchema = z.object({
  created: z.number().int().nonnegative(),
  linkedToEarlierApplication: z.number().int().nonnegative(),
  filled: z.number().int().nonnegative(),
  skippedDuplicates: z.number().int().nonnegative(),
  invalid: z.number().int().nonnegative(),
});
export type CandidateImportResult = z.infer<typeof candidateImportResultSchema>;

// ---------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------

export const sourceBreakdownEntrySchema = z.object({
  source: candidateSourceSchema,
  count: z.number().int().nonnegative(),
  pct: z.number().min(0).max(100),
});

export const funnelStageEntrySchema = z.object({
  stage: candidateStageSchema,
  count: z.number().int().nonnegative(),
});

/** How many candidates ever reached a stage type, and what share of those who applied. */
export const funnelConversionEntrySchema = z.object({
  stage: candidateStageSchema,
  reached: z.number().int().nonnegative(),
  pctOfApplied: z.number().min(0).max(100),
});

export const timeInStageEntrySchema = z.object({
  stage: candidateStageSchema,
  avgDays: z.number().nonnegative().nullable(),
  /** Candidates the average is over. */
  samples: z.number().int().nonnegative(),
});

export const sourceEffectivenessEntrySchema = z.object({
  source: candidateSourceSchema,
  applied: z.number().int().nonnegative(),
  hired: z.number().int().nonnegative(),
  hireRatePct: z.number().min(0).max(100),
});

export const recruiterLoadEntrySchema = z.object({
  personId: uuidSchema,
  name: z.string(),
  role: z.enum(HIRING_TEAM_ROLES),
  openRequisitions: z.number().int().nonnegative(),
  activeCandidates: z.number().int().nonnegative(),
});

export const interviewerLoadEntrySchema = z.object({
  personId: uuidSchema,
  name: z.string(),
  interviews: z.number().int().nonnegative(),
  scorecardsPending: z.number().int().nonnegative(),
});

export const requisitionAgeingEntrySchema = z.object({
  requisitionId: uuidSchema,
  title: z.string(),
  openDays: z.number().int().nonnegative(),
  seatsOpen: z.number().int().nonnegative(),
});

export const recruitmentAnalyticsSchema = z.object({
  /** Applied to offer accepted, in days. */
  timeToHireAvgDays: z.number().nonnegative().nullable(),
  /** Requisition opened to its last seat filled, in days, over requisitions fully filled. */
  timeToFillAvgDays: z.number().nonnegative().nullable(),
  funnelConversion: z.array(funnelConversionEntrySchema),
  timeInStage: z.array(timeInStageEntrySchema),
  sourceEffectiveness: z.array(sourceEffectivenessEntrySchema),
  recruiterLoad: z.array(recruiterLoadEntrySchema),
  interviewerLoad: z.array(interviewerLoadEntrySchema),
  requisitionAgeing: z.array(requisitionAgeingEntrySchema),
  /** Hires made in the period; the denominator of `costPerHireAvg`. */
  hiresInPeriod: z.number().int().nonnegative(),
  offerAcceptanceRatePct: z.number().min(0).max(100).nullable(),
  costPerHireAvg: decimalStringSchema.nullable(),
  costPerHireCurrency: currencySchema,
  activePipelineCount: z.number().int().nonnegative(),
  sourceBreakdown: z.array(sourceBreakdownEntrySchema),
  funnelStages: z.array(funnelStageEntrySchema),
  generatedAt: instantSchema,
});
export type RecruitmentAnalytics = z.infer<typeof recruitmentAnalyticsSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export const listRequisitionsQuerySchema = paginationQuerySchema.extend({
  status: requisitionStatusSchema.optional(),
  departmentId: uuidSchema.optional(),
  legalEntityId: uuidSchema.optional(),
});

export const createRequisitionBodySchema = z.object({
  legalEntityId: uuidSchema,
  departmentId: uuidSchema,
  locationId: uuidSchema.optional(),
  title: z.string().trim().min(1).max(200),
  headcount: z.number().int().positive().max(1000),
  employmentType: employmentTypeSchema,
  workType: workTypeSchema,
  minSalary: decimalStringSchema.optional(),
  maxSalary: decimalStringSchema.optional(),
  currency: currencySchema,
  targetHiringDate: dateOnlySchema.optional(),
  hiringManagerPersonId: uuidSchema.optional(),
});

export const updateRequisitionBodySchema = createRequisitionBodySchema.partial().extend({
  /** `null` removes the hiring manager. */
  hiringManagerPersonId: uuidSchema.nullable().optional(),
});

export const decideRequisitionBodySchema = z.object({
  comment: z.string().max(500).optional(),
});

export const listPostingsQuerySchema = paginationQuerySchema.extend({
  requisitionId: uuidSchema.optional(),
  status: jobPostingStatusSchema.optional(),
});

export const createPostingBodySchema = z.object({
  requisitionId: uuidSchema,
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().min(1).max(20_000),
  /** What the role does vs. what we're looking for — see schema.prisma's JobPosting.requirements
   *  doc comment for why this is a separate field rather than one description blob. */
  requirements: z.string().trim().max(20_000).optional(),
  isPublic: z.boolean().default(true),
  /** Open to current employees on the internal job board too. */
  isInternal: z.boolean().default(false),
  /** Omitted: the organisation's default hiring flow. */
  hiringFlowId: uuidSchema.optional(),
});

export const updatePostingBodySchema = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  description: z.string().trim().min(1).max(20_000).optional(),
  /** `null` clears a previously-set value; `undefined` (the field simply absent) leaves it
   *  untouched — same optional-vs-null convention as the rest of this module's partial updates. */
  requirements: z.string().trim().max(20_000).nullable().optional(),
  isPublic: z.boolean().optional(),
  isInternal: z.boolean().optional(),
  /** Changing it moves the posting's candidates to the same-type stage of the new flow. `null`
   *  returns to the organisation's default flow. */
  hiringFlowId: uuidSchema.nullable().optional(),
});

export const listCandidatesQuerySchema = paginationQuerySchema.extend({
  postingId: uuidSchema.optional(),
  stage: candidateStageSchema.optional(),
  /** One named stage of a hiring flow (a board column). */
  stageId: uuidSchema.optional(),
  status: candidateStatusSchema.optional(),
  source: candidateSourceSchema.optional(),
  /** Candidates sent by one recruitment agency. */
  agencyId: uuidSchema.optional(),
  q: z.string().trim().max(200).optional(),
  /** `best_match` puts the highest skills-and-years match first (people with no score last). */
  sort: z.enum(['newest', 'best_match']).default('newest'),
  /** Only candidates whose match is at least this. */
  minMatch: z.coerce.number().int().min(0).max(100).optional(),
});

export const createCandidateBodySchema = z
  .object({
    postingId: uuidSchema,
    firstName: z.string().trim().min(1).max(100),
    lastName: z.string().trim().min(1).max(100),
    email: emailSchema,
    phone: phoneSchema.optional(),
    resumeUrl: z.string().url().optional(),
    linkedinUrl: z.string().url().optional(),
    source: candidateSourceSchema.default('direct'),
    /** Which recruitment agency sent the profile; only with `source: 'agency'`. */
    agencyId: uuidSchema.optional(),
  })
  .refine((body) => body.agencyId === undefined || body.source === 'agency', {
    message: 'An agency can only be set when the source is Agency',
    path: ['agencyId'],
  });

export const referCandidateBodySchema = z.object({
  postingId: uuidSchema,
  firstName: z.string().trim().min(1).max(100),
  lastName: z.string().trim().min(1).max(100),
  email: emailSchema,
  phone: phoneSchema.optional(),
  resumeUrl: z.string().url().optional(),
  note: z.string().max(1000).optional(),
});

/** Move between the open stages, by flow stage or (all-postings board) by stage type. Hired and
 *  Rejected are never a move — see `stageMoveRefusal`. */
export const moveCandidateStageBodySchema = z
  .object({
    stageId: uuidSchema.optional(),
    stage: candidateStageSchema.optional(),
  })
  .refine((b) => (b.stageId !== undefined) !== (b.stage !== undefined), {
    message: 'Give a stageId or a stage',
    path: ['stageId'],
  });

export const withdrawCandidateBodySchema = z.object({
  reason: z.string().max(500).optional(),
});

export const setCandidateRatingBodySchema = z.object({
  rating: z.number().int().min(1).max(5).nullable(),
});

export const listInterviewsQuerySchema = paginationQuerySchema.extend({
  candidateId: uuidSchema.optional(),
  interviewerPersonId: uuidSchema.optional(),
  status: interviewStatusSchema.optional(),
  from: instantSchema.optional(),
  to: instantSchema.optional(),
});

export const scheduleInterviewBodySchema = z.object({
  candidateId: uuidSchema,
  roundName: z.string().trim().min(1).max(100),
  interviewerPersonId: uuidSchema,
  scheduledAt: instantSchema,
  durationMinutes: z.number().int().positive().max(480).default(60),
  meetingUrl: z.string().url().optional(),
  meetingProvider: meetingProviderSchema.default('manual'),
  /** More interviewers on the same round (a panel), besides `interviewerPersonId`. */
  panelPersonIds: z.array(uuidSchema).max(10).optional(),
  /** The scorecard the panel fills in. Omitted: the org's default template, if any. */
  scorecardTemplateId: uuidSchema.optional(),
});

export const rescheduleInterviewBodySchema = z.object({
  roundName: z.string().trim().min(1).max(100).optional(),
  interviewerPersonId: uuidSchema.optional(),
  /** Replaces the rest of the panel (the lead stays). */
  panelPersonIds: z.array(uuidSchema).max(10).optional(),
  scheduledAt: instantSchema.optional(),
  durationMinutes: z.number().int().positive().max(480).optional(),
  meetingUrl: z.string().url().nullable().optional(),
  meetingProvider: meetingProviderSchema.optional(),
});

export const cancelInterviewBodySchema = z.object({
  reason: z.string().max(500).optional(),
});

/** Always filed as the caller — there is no "on behalf of" (bug 13). With a template, `ratings`
 *  covers each criterion and the overall `rating` defaults to their rounded average. */
export const submitScorecardBodySchema = z
  .object({
    rating: z.number().int().min(1).max(5).optional(),
    recommendation: interviewRecommendationSchema,
    notes: z.string().max(4000).optional(),
    ratings: z
      .array(
        z.object({
          criterionId: z.string().trim().min(1).max(64),
          rating: z.number().int().min(1).max(5),
          note: z.string().trim().max(1000).optional(),
        }),
      )
      .max(200)
      .optional(),
  })
  .refine((b) => b.rating !== undefined || (b.ratings?.length ?? 0) > 0, {
    message: 'Give an overall rating or rate the criteria',
    path: ['rating'],
  });

export const listOffersQuerySchema = paginationQuerySchema.extend({
  candidateId: uuidSchema.optional(),
  status: offerStatusSchema.optional(),
});

export const createOfferBodySchema = z
  .object({
    candidateId: uuidSchema,
    /** Defaults to the posting's own title when omitted — see schema.prisma's doc comment. */
    designation: z.string().trim().max(200).optional(),
    annualCtc: decimalStringSchema,
    currency: currencySchema,
    targetJoinDate: dateOnlySchema,
    sourceType: offerSourceTypeSchema.default('template'),
    /** `sourceType: 'template'` only — applies the template's {{TOKEN}}-substituted text as
     *  `renderedBody` at creation time. Optional even then: an offer can be created with no
     *  template, the plain CTC/date fields alone. */
    templateId: uuidSchema.optional(),
    /** `sourceType: 'template'` only — values for any {{CUSTOM}} tokens the chosen template's body
     *  references beyond the five fixed ones (CANDIDATE_NAME/DESIGNATION/JOIN_DATE/ANNUAL_CTC/
     *  ORG_NAME, which are always computed server-side and win over a same-named custom key). Keys
     *  are the bare token name, e.g. `{"PROBATION_MONTHS": "3"}` for a `{{PROBATION_MONTHS}}` in
     *  the body. */
    customVariables: z
      .record(z.string().trim().min(1).max(64), z.string().trim().max(4000))
      .optional(),
    /** `sourceType: 'upload'` only — the URL `POST /recruitment/offers/upload` handed back. */
    documentUrl: z.string().url().optional(),
    /** The hire's work (login) email, when already known. */
    workEmail: emailSchema.optional(),
    /** The CTC template to build the hire's salary structure from; must be active, in the offer's
     *  currency, and fit the CTC. */
    ctcTemplateId: uuidSchema.optional(),
    /** Required when the CTC is outside the requisition's salary band. Shown to the approvers. */
    bandExceptionReason: z.string().trim().min(3).max(500).optional(),
    /** The company signatory whose name and signature the letter carries; omitted: the default. */
    signatoryId: uuidSchema.optional(),
  })
  .refine((body) => body.sourceType !== 'upload' || !!body.documentUrl, {
    message: 'documentUrl is required when sourceType is "upload"',
    path: ['documentUrl'],
  });

/** How long the candidate has to respond, from the moment the offer is sent. */
export const OFFER_EXPIRY_DEFAULT_DAYS = 7;
export const sendOfferBodySchema = z.object({
  expiresInDays: z.number().int().min(1).max(60).default(OFFER_EXPIRY_DEFAULT_DAYS),
});

/** What a revision changes; everything left out stays as it was. A new salary or designation sends
 *  the offer back through approval; changing only the join date or work email does not. */
export const reviseOfferBodySchema = z.object({
  annualCtc: decimalStringSchema.optional(),
  designation: z.string().trim().min(1).max(200).optional(),
  targetJoinDate: dateOnlySchema.optional(),
  /** `null` drops the salary structure. */
  ctcTemplateId: uuidSchema.nullable().optional(),
  workEmail: emailSchema.nullable().optional(),
  bandExceptionReason: z.string().trim().min(3).max(500).optional(),
  /** `sourceType: 'upload'` offers: the new PDF (the URL the upload route handed back). */
  documentUrl: z.string().url().optional(),
  /** Why it is being revised — shown on the timeline. */
  reason: z.string().trim().max(500).optional(),
});

/** A PNG or JPEG data URL, small enough to sit in the letter. */
const signatureImageSchema = z
  .string()
  .max(300_000)
  .regex(/^data:image\/(png|jpeg);base64,[A-Za-z0-9+/=]+$/, 'Upload a PNG or JPEG image');

export const offerSignatorySchema = z.object({
  id: uuidSchema,
  name: z.string(),
  designation: z.string(),
  signatureImage: z.string().nullable(),
  isDefault: z.boolean(),
  isActive: z.boolean(),
});
export type OfferSignatory = z.infer<typeof offerSignatorySchema>;

export const createOfferSignatoryBodySchema = z.object({
  name: z.string().trim().min(1).max(120),
  designation: z.string().trim().min(1).max(120),
  signatureImage: signatureImageSchema.optional(),
  isDefault: z.boolean().default(false),
});
export const updateOfferSignatoryBodySchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  designation: z.string().trim().min(1).max(120).optional(),
  signatureImage: signatureImageSchema.nullable().optional(),
  isDefault: z.boolean().optional(),
  isActive: z.boolean().optional(),
});

/** A salary structure an offer can be built on — the minimum a recruiter needs to pick one. */
export const offerCtcTemplateSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  name: z.string(),
  currency: currencySchema,
  minAnnualCtc: decimalStringSchema.nullable(),
  maxAnnualCtc: decimalStringSchema.nullable(),
});
export type OfferCtcTemplate = z.infer<typeof offerCtcTemplateSchema>;

// ---- Internal job board (Keka "internal job portal") ------------------------------

export const internalJobSchema = z.object({
  id: uuidSchema,
  title: z.string(),
  description: z.string(),
  requirements: z.string().nullable(),
  departmentName: z.string(),
  locationName: z.string().nullable(),
  workType: workTypeSchema,
  employmentType: employmentTypeSchema,
  postedAt: instantSchema,
  /** The signed-in employee has already applied. */
  alreadyApplied: z.boolean(),
});
export type InternalJob = z.infer<typeof internalJobSchema>;

export const listInternalJobsQuerySchema = paginationQuerySchema.extend({
  q: z.string().trim().max(200).optional(),
});

export const applyToInternalJobBodySchema = z.object({
  coverNote: z.string().trim().max(2000).optional(),
});

export const decideOfferBodySchema = z.object({
  comment: z.string().max(500).optional(),
});

export const rescindOfferBodySchema = z.object({
  reason: z.string().max(500).optional(),
});

export const recruitmentAnalyticsQuerySchema = z.object({
  legalEntityId: uuidSchema.optional(),
  fromDate: dateOnlySchema.optional(),
  toDate: dateOnlySchema.optional(),
});

export const listOfferTemplatesQuerySchema = paginationQuerySchema.extend({
  isActive: queryBooleanSchema.optional(),
});

export const createOfferTemplateBodySchema = z.object({
  name: z.string().trim().min(1).max(200),
  body: z.string().trim().min(1).max(20_000),
  isDefault: z.boolean().default(false),
  letterheadUrls: z.array(z.string().url()).max(5).optional(),
});

export const updateOfferTemplateBodySchema = createOfferTemplateBodySchema.partial().extend({
  isActive: z.boolean().optional(),
});

export const createSlotOfferBodySchema = z.object({
  candidateId: uuidSchema,
  roundName: z.string().trim().min(1).max(100),
  interviewerPersonId: uuidSchema,
  durationMinutes: z.number().int().positive().max(480).default(60),
  /** Recruiter-entered proposed times — not computed from anyone's real calendar. */
  slots: z.array(instantSchema).min(1).max(10),
});

export const listSlotOffersQuerySchema = paginationQuerySchema.extend({
  candidateId: uuidSchema.optional(),
  status: slotOfferStatusSchema.optional(),
});

export const setReferralBonusBodySchema = z.object({
  amount: decimalStringSchema.refine((v) => !v.startsWith('-'), 'A bonus cannot be negative'),
  currency: currencySchema,
});

/** Only `paid` is set by hand now; `approved`/`rejected` come from the approval workflow. */
export const updateReferralBonusStatusBodySchema = z.object({
  status: z.literal('paid'),
});

export const decideReferralBonusBodySchema = z.object({
  comment: z.string().max(500).optional(),
});

export const listAssessmentTemplatesQuerySchema = paginationQuerySchema.extend({
  isActive: queryBooleanSchema.optional(),
});

export const createAssessmentTemplateBodySchema = z.object({
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2000).optional(),
  questions: z.array(assessmentQuestionSchema).min(1).max(50),
});

export const updateAssessmentTemplateBodySchema = createAssessmentTemplateBodySchema
  .partial()
  .extend({
    isActive: z.boolean().optional(),
  });

export const listCandidateAssessmentsQuerySchema = paginationQuerySchema.extend({
  candidateId: uuidSchema.optional(),
  status: assessmentStatusSchema.optional(),
});

export const sendAssessmentBodySchema = z.object({
  candidateId: uuidSchema,
  templateId: uuidSchema,
  /** Defaults to 14 in the handler — same "recruiter can widen it, system has a sane default"
   *  posture as InterviewSlotOffer's implicit expiry. */
  expiresInDays: z.number().int().positive().max(60).optional(),
});

export const listTalentPoolQuerySchema = paginationQuerySchema.extend({
  candidateId: uuidSchema.optional(),
  skillTag: z.string().trim().max(100).optional(),
  q: z.string().trim().max(200).optional(),
});

export const addToTalentPoolBodySchema = z.object({
  candidateId: uuidSchema,
  skillTags: z.array(z.string().trim().min(1).max(100)).min(1).max(20),
  reason: z.string().trim().max(1000).optional(),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const recruitmentContract = c.router(
  {
    // ---- Requisitions ---------------------------------------------------
    listRequisitions: {
      method: 'GET',
      path: '/requisitions',
      query: listRequisitionsQuerySchema,
      responses: { 200: paginatedSchema(jobRequisitionSchema), 401: errorBodySchema },
      summary: "List job requisitions in the caller's visible scope",
      metadata: routeMeta(null),
    },
    getRequisition: {
      method: 'GET',
      path: '/requisitions/:requisitionId',
      responses: { 200: jobRequisitionSchema, 401: errorBodySchema, 404: errorBodySchema },
      summary: 'Get one job requisition',
      metadata: routeMeta('requisition:read'),
    },
    createRequisition: {
      method: 'POST',
      path: '/requisitions',
      headers: z.object({ 'idempotency-key': idempotencyKeySchema }),
      body: createRequisitionBodySchema,
      responses: {
        201: jobRequisitionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Draft a headcount request',
      metadata: routeMeta('requisition:create'),
    },
    updateRequisition: {
      method: 'PATCH',
      path: '/requisitions/:requisitionId',
      body: updateRequisitionBodySchema,
      responses: {
        200: jobRequisitionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Edit a draft requisition (before it is submitted for approval)',
      metadata: routeMeta('requisition:update'),
    },
    submitRequisition: {
      method: 'POST',
      path: '/requisitions/:requisitionId/submit',
      body: c.noBody(),
      responses: {
        200: jobRequisitionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Submit a draft requisition and start its approval workflow',
      metadata: routeMeta('requisition:update'),
    },
    approveRequisition: {
      method: 'POST',
      path: '/requisitions/:requisitionId/approve',
      body: decideRequisitionBodySchema,
      responses: {
        200: jobRequisitionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Approve the step currently awaiting the caller',
      metadata: routeMeta(null),
    },
    rejectRequisition: {
      method: 'POST',
      path: '/requisitions/:requisitionId/reject',
      body: decideRequisitionBodySchema,
      responses: {
        200: jobRequisitionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Reject the step currently awaiting the caller',
      metadata: routeMeta(null),
    },
    closeRequisition: {
      method: 'POST',
      path: '/requisitions/:requisitionId/close',
      body: c.noBody(),
      responses: {
        200: jobRequisitionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Close a requisition (headcount filled or request withdrawn)',
      metadata: routeMeta('requisition:close'),
    },

    // ---- Postings ---------------------------------------------------------
    listPostings: {
      method: 'GET',
      path: '/postings',
      query: listPostingsQuerySchema,
      responses: { 200: paginatedSchema(jobPostingSchema), 401: errorBodySchema },
      summary: 'List job postings',
      metadata: routeMeta(null),
    },
    getPosting: {
      method: 'GET',
      path: '/postings/:postingId',
      responses: { 200: jobPostingSchema, 401: errorBodySchema, 404: errorBodySchema },
      summary: 'Get one job posting',
      metadata: routeMeta(null),
    },
    createPosting: {
      method: 'POST',
      path: '/postings',
      body: createPostingBodySchema,
      responses: {
        201: jobPostingSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Publish a job posting against an approved requisition',
      metadata: routeMeta(['requisition:read', 'requisition:update']),
    },
    updatePosting: {
      method: 'PATCH',
      path: '/postings/:postingId',
      body: updatePostingBodySchema,
      responses: {
        200: jobPostingSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Edit a job posting',
      metadata: routeMeta(['requisition:read', 'requisition:update']),
    },
    closePosting: {
      method: 'POST',
      path: '/postings/:postingId/close',
      body: c.noBody(),
      responses: {
        200: jobPostingSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Close a job posting (removes it from the public career site)',
      metadata: routeMeta(['requisition:read', 'requisition:update']),
    },

    // ---- Candidates ---------------------------------------------------------
    listCandidates: {
      method: 'GET',
      path: '/candidates',
      query: listCandidatesQuerySchema,
      responses: { 200: paginatedSchema(candidateSchema), 401: errorBodySchema },
      summary: 'List candidates in the pipeline',
      metadata: routeMeta(null),
    },
    getCandidate: {
      method: 'GET',
      path: '/candidates/:candidateId',
      responses: { 200: candidateSchema, 401: errorBodySchema, 404: errorBodySchema },
      summary: 'Get one candidate',
      metadata: routeMeta('candidate:read'),
    },
    downloadCandidateResume: {
      method: 'GET',
      path: '/candidates/:candidateId/resume',
      responses: { 200: recruitmentFileSchema, 401: errorBodySchema, 404: errorBodySchema },
      summary: "Download a candidate's uploaded resume",
      // A whole file as base64 is no use in a prompt; the copilot can navigate to the page instead.
      metadata: routeMeta('candidate:read', { ai: 'hidden' }),
    },
    createCandidate: {
      method: 'POST',
      path: '/candidates',
      body: createCandidateBodySchema,
      responses: {
        201: candidateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Manually add a sourced candidate to a posting',
      metadata: routeMeta('candidate:manage'),
    },
    referCandidate: {
      method: 'POST',
      path: '/candidates/refer',
      body: referCandidateBodySchema,
      responses: {
        201: candidateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Refer a candidate to an open posting (Employee Referral Portal)',
      metadata: routeMeta('referral:create'),
    },
    listMyReferrals: {
      method: 'GET',
      path: '/my-referrals',
      query: paginationQuerySchema,
      responses: { 200: paginatedSchema(candidateSchema), 401: errorBodySchema },
      summary: "List the caller's own referrals and their pipeline status",
      metadata: routeMeta('referral:read'),
    },
    moveCandidateStage: {
      method: 'POST',
      path: '/candidates/:candidateId/stage',
      body: moveCandidateStageBodySchema,
      responses: {
        200: candidateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Move a candidate to a different pipeline stage',
      metadata: routeMeta(['candidate:manage', 'candidate:read']),
    },
    withdrawCandidate: {
      method: 'POST',
      path: '/candidates/:candidateId/withdraw',
      body: withdrawCandidateBodySchema,
      responses: {
        200: candidateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Mark a candidate as withdrawn',
      metadata: routeMeta(['candidate:manage', 'candidate:read']),
    },
    rejectCandidate: {
      method: 'POST',
      path: '/candidates/:candidateId/reject',
      body: rejectCandidateBodySchema,
      responses: {
        200: candidateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Reject a candidate with a reason: closes their interviews, assessment links and offers, and can send the rejection email',
      metadata: routeMeta(['candidate:manage', 'candidate:read']),
    },
    reconsiderCandidate: {
      method: 'POST',
      path: '/candidates/:candidateId/reconsider',
      body: reconsiderCandidateBodySchema,
      responses: {
        200: candidateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Reopen a rejected candidate into an open stage',
      metadata: routeMeta(['candidate:manage', 'candidate:read']),
    },
    bulkMoveCandidates: {
      method: 'POST',
      path: '/candidates/bulk-move',
      body: bulkMoveCandidatesBodySchema,
      responses: { 200: bulkResultSchema, 401: errorBodySchema, 403: errorBodySchema, 422: errorBodySchema },
      summary: 'Move several candidates to one stage; each is checked by the same rules as a single move',
      metadata: routeMeta(['candidate:manage', 'candidate:read']),
    },
    bulkRejectCandidates: {
      method: 'POST',
      path: '/candidates/bulk-reject',
      body: bulkRejectCandidatesBodySchema,
      responses: { 200: bulkResultSchema, 401: errorBodySchema, 403: errorBodySchema, 422: errorBodySchema },
      summary: 'Reject several candidates with one reason',
      metadata: routeMeta(['candidate:manage', 'candidate:read']),
    },
    listCandidateActivity: {
      method: 'GET',
      path: '/candidates/:candidateId/activity',
      query: paginationQuerySchema,
      responses: {
        200: paginatedSchema(candidateActivitySchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "A candidate's timeline: stage changes, interviews, offers, notes and emails, newest first",
      metadata: routeMeta('candidate:read'),
    },
    listCandidateNotes: {
      method: 'GET',
      path: '/candidates/:candidateId/notes',
      query: paginationQuerySchema,
      responses: {
        200: paginatedSchema(candidateNoteSchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "Notes the hiring team has left on a candidate",
      metadata: routeMeta('candidate:read'),
    },
    listMentionablePeople: {
      method: 'GET',
      path: '/candidates/:candidateId/mentionable',
      responses: {
        200: z.array(z.object({ personId: uuidSchema, name: z.string() })),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'The hiring-team members who can be @mentioned in a note on this candidate',
      metadata: routeMeta('candidate:read'),
    },
    addCandidateNote: {
      method: 'POST',
      path: '/candidates/:candidateId/notes',
      body: addCandidateNoteBodySchema,
      responses: {
        201: candidateNoteSchema,
        401: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Add a note to a candidate; people @mentioned from the hiring team are notified',
      metadata: routeMeta('candidate:read'),
    },
    deleteCandidateNote: {
      method: 'DELETE',
      path: '/candidates/:candidateId/notes/:noteId',
      responses: {
        200: z.object({ deleted: z.literal(true) }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Delete a note: its author, or whoever manages candidates',
      metadata: routeMeta('candidate:read'),
    },
    listCandidateEmails: {
      method: 'GET',
      path: '/candidates/:candidateId/emails',
      query: paginationQuerySchema,
      responses: {
        200: paginatedSchema(candidateEmailSchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Emails sent to a candidate from the app, including ones waiting for their send time',
      metadata: routeMeta(['candidate:manage', 'candidate:read']),
    },
    sendCandidateEmail: {
      method: 'POST',
      path: '/candidates/send-email',
      body: sendCandidateEmailBodySchema,
      responses: {
        200: sendCandidateEmailResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Email one or many candidates; {{merge fields}} are filled per candidate and replies go to the sender',
      metadata: routeMeta(['candidate:manage', 'candidate:read']),
    },
    previewCandidateEmail: {
      method: 'POST',
      path: '/candidates/preview-email',
      body: previewCandidateEmailBodySchema,
      responses: {
        200: candidateEmailPreviewSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'See an email as one candidate would get it',
      metadata: routeMeta(['candidate:manage', 'candidate:read']),
    },
    listHiringFlows: {
      method: 'GET',
      path: '/hiring-flows',
      responses: { 200: z.array(hiringFlowSchema), 401: errorBodySchema },
      summary: 'Hiring flows, with how many candidates are in each stage',
      metadata: routeMeta('candidate:read'),
    },
    createHiringFlow: {
      method: 'POST',
      path: '/hiring-flows',
      body: createHiringFlowBodySchema,
      responses: { 201: hiringFlowSchema, 401: errorBodySchema, 403: errorBodySchema, 422: errorBodySchema },
      summary: 'Create a hiring flow',
      metadata: routeMeta('candidate:manage'),
    },
    updateHiringFlow: {
      method: 'PATCH',
      path: '/hiring-flows/:flowId',
      body: updateHiringFlowBodySchema,
      responses: {
        200: hiringFlowSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Edit a hiring flow; a stage with candidates in it can only be removed once they are moved',
      metadata: routeMeta('candidate:manage'),
    },
    getPipelineSummary: {
      method: 'GET',
      path: '/pipeline',
      query: pipelineSummaryQuerySchema,
      responses: { 200: pipelineSummarySchema, 401: errorBodySchema, 404: errorBodySchema },
      summary: "The board's columns with candidate counts, for one posting or all",
      metadata: routeMeta('candidate:read'),
    },
    listRejectionReasons: {
      method: 'GET',
      path: '/rejection-reasons',
      responses: { 200: z.array(rejectionReasonSchema), 401: errorBodySchema },
      summary: 'The reasons a recruiter can pick when rejecting a candidate',
      metadata: routeMeta('candidate:read'),
    },
    createRejectionReason: {
      method: 'POST',
      path: '/rejection-reasons',
      body: createRejectionReasonBodySchema,
      responses: { 201: rejectionReasonSchema, 401: errorBodySchema, 403: errorBodySchema, 422: errorBodySchema },
      summary: 'Add a rejection reason',
      metadata: routeMeta('candidate:manage'),
    },
    updateRejectionReason: {
      method: 'PATCH',
      path: '/rejection-reasons/:reasonId',
      body: updateRejectionReasonBodySchema,
      responses: {
        200: rejectionReasonSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Rename or switch off a rejection reason (reasons are never deleted: history keeps them)',
      metadata: routeMeta('candidate:manage'),
    },
    listCandidateEmailTemplates: {
      method: 'GET',
      path: '/candidate-email-templates',
      responses: { 200: z.array(candidateEmailTemplateSchema), 401: errorBodySchema },
      summary: 'Email templates for messages to candidates, including the rejection email',
      metadata: routeMeta('candidate:manage'),
    },
    createCandidateEmailTemplate: {
      method: 'POST',
      path: '/candidate-email-templates',
      body: createCandidateEmailTemplateBodySchema,
      responses: {
        201: candidateEmailTemplateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Create a candidate email template',
      metadata: routeMeta('candidate:manage'),
    },
    updateCandidateEmailTemplate: {
      method: 'PATCH',
      path: '/candidate-email-templates/:templateId',
      body: updateCandidateEmailTemplateBodySchema,
      responses: {
        200: candidateEmailTemplateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Edit a candidate email template',
      metadata: routeMeta('candidate:manage'),
    },
    setCandidateRating: {
      method: 'POST',
      path: '/candidates/:candidateId/rating',
      body: setCandidateRatingBodySchema,
      responses: {
        200: candidateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "Set the recruiter's own overall rating for a candidate",
      metadata: routeMeta(['candidate:manage', 'candidate:read']),
    },

    // ---- Interviews ---------------------------------------------------------
    listInterviews: {
      method: 'GET',
      path: '/interviews',
      query: listInterviewsQuerySchema,
      responses: { 200: paginatedSchema(interviewScheduleSchema), 401: errorBodySchema },
      summary: 'List scheduled interviews',
      metadata: routeMeta(null),
    },
    getInterview: {
      method: 'GET',
      path: '/interviews/:interviewId',
      responses: { 200: interviewScheduleSchema, 401: errorBodySchema, 404: errorBodySchema },
      summary: 'Get one interview',
      metadata: routeMeta('interview:read'),
    },
    scheduleInterview: {
      method: 'POST',
      path: '/interviews',
      body: scheduleInterviewBodySchema,
      responses: {
        201: interviewScheduleSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Schedule an interview round for a candidate',
      metadata: routeMeta(['candidate:read', 'interview:schedule']),
    },
    rescheduleInterview: {
      method: 'PATCH',
      path: '/interviews/:interviewId',
      body: rescheduleInterviewBodySchema,
      responses: {
        200: interviewScheduleSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Reschedule an interview',
      metadata: routeMeta(['interview:read', 'interview:schedule']),
    },
    cancelInterview: {
      method: 'POST',
      path: '/interviews/:interviewId/cancel',
      body: cancelInterviewBodySchema,
      responses: {
        200: interviewScheduleSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Cancel a scheduled interview',
      metadata: routeMeta(['interview:read', 'interview:schedule']),
    },
    submitScorecard: {
      method: 'POST',
      path: '/interviews/:interviewId/scorecard',
      body: submitScorecardBodySchema,
      responses: {
        200: interviewScheduleSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Submit the interviewer scorecard for a completed interview',
      // Anyone on the interview's panel, whatever their role: the handler checks the panel.
      metadata: routeMeta(null),
    },
    listInterviewScorecards: {
      method: 'GET',
      path: '/interviews/:interviewId/scorecards',
      responses: { 200: scorecardSummarySchema, 401: errorBodySchema, 404: errorBodySchema },
      summary: "One interview's scorecards (yours first, until you have filed it)",
      metadata: routeMeta(null),
    },
    getCandidateScorecards: {
      method: 'GET',
      path: '/candidates/:candidateId/scorecards',
      responses: { 200: scorecardSummarySchema, 401: errorBodySchema, 404: errorBodySchema },
      summary: "Every scorecard for a candidate, with averages by round, interviewer and section",
      metadata: routeMeta(null),
    },

    // ---- My interviews -----------------------------------------------------
    listMyInterviews: {
      method: 'GET',
      path: '/my-interviews',
      query: listMyInterviewsQuerySchema,
      responses: { 200: paginatedSchema(myInterviewSchema), 401: errorBodySchema },
      summary: 'Interviews I am on the panel for',
      metadata: routeMeta(null),
    },
    getMyInterview: {
      method: 'GET',
      path: '/my-interviews/:interviewId',
      responses: { 200: myInterviewDetailSchema, 401: errorBodySchema, 404: errorBodySchema },
      summary: 'One of my interviews: the candidate, the interview kit and my scorecard',
      metadata: routeMeta(null),
    },
    downloadMyInterviewResume: {
      method: 'GET',
      path: '/my-interviews/:interviewId/resume',
      responses: { 200: recruitmentFileSchema, 401: errorBodySchema, 404: errorBodySchema },
      summary: "Download the resume of a candidate I'm interviewing",
      metadata: routeMeta(null, { ai: 'hidden' }),
    },

    // ---- Hiring team -------------------------------------------------------
    listRequisitionMembers: {
      method: 'GET',
      path: '/requisitions/:requisitionId/members',
      responses: {
        200: z.array(requisitionMemberSchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "A requisition's hiring team",
      metadata: routeMeta('requisition:read'),
    },
    setRequisitionMembers: {
      method: 'PUT',
      path: '/requisitions/:requisitionId/members',
      body: setRequisitionMembersBodySchema,
      responses: {
        200: z.array(requisitionMemberSchema),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: "Replace a requisition's hiring team (hiring manager, recruiters, coordinators, interviewers)",
      metadata: routeMeta('requisition:update'),
    },

    // ---- Scorecard templates -----------------------------------------------
    listScorecardTemplates: {
      method: 'GET',
      path: '/scorecard-templates',
      query: listScorecardTemplatesQuerySchema,
      responses: { 200: paginatedSchema(scorecardTemplateSchema), 401: errorBodySchema },
      summary: 'Scorecard templates (sections, criteria, rating scale, interview kit)',
      metadata: routeMeta('interview:read'),
    },
    getScorecardTemplate: {
      method: 'GET',
      path: '/scorecard-templates/:templateId',
      responses: { 200: scorecardTemplateSchema, 401: errorBodySchema, 404: errorBodySchema },
      summary: 'One scorecard template',
      metadata: routeMeta('interview:read'),
    },
    createScorecardTemplate: {
      method: 'POST',
      path: '/scorecard-templates',
      body: createScorecardTemplateBodySchema,
      responses: {
        201: scorecardTemplateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Create a scorecard template',
      metadata: routeMeta('candidate:manage'),
    },
    updateScorecardTemplate: {
      method: 'PATCH',
      path: '/scorecard-templates/:templateId',
      body: updateScorecardTemplateBodySchema,
      responses: {
        200: scorecardTemplateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Edit a scorecard template; interviews already scheduled keep the version they had',
      metadata: routeMeta('candidate:manage'),
    },

    // ---- Offers ---------------------------------------------------------
    listOffers: {
      method: 'GET',
      path: '/offers',
      query: listOffersQuerySchema,
      responses: { 200: paginatedSchema(offerLetterSchema), 401: errorBodySchema },
      summary: 'List offer letters',
      metadata: routeMeta(null, { salaryPin: 'others' }),
    },
    getOffer: {
      method: 'GET',
      path: '/offers/:offerId',
      responses: { 200: offerLetterSchema, 401: errorBodySchema, 404: errorBodySchema },
      summary: 'Get one offer letter',
      metadata: routeMeta('offer:create', { salaryPin: 'others' }),
    },
    downloadOfferDocument: {
      method: 'GET',
      path: '/offers/:offerId/document',
      responses: { 200: recruitmentFileSchema, 401: errorBodySchema, 404: errorBodySchema },
      summary: "Download an offer's uploaded PDF",
      metadata: routeMeta(['offer:create', 'offer.compensation:read'], { ai: 'hidden', salaryPin: 'others' }),
    },
    createOffer: {
      method: 'POST',
      path: '/offers',
      headers: z.object({ 'idempotency-key': idempotencyKeySchema }),
      body: createOfferBodySchema,
      responses: {
        201: offerLetterSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Draft an offer letter for a candidate',
      metadata: routeMeta(['candidate:read', 'offer:create'], { salaryPin: 'others' }),
    },
    submitOffer: {
      method: 'POST',
      path: '/offers/:offerId/submit',
      body: c.noBody(),
      responses: {
        200: offerLetterSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Submit a draft offer and start its approval workflow',
      metadata: routeMeta('offer:create', { salaryPin: 'others' }),
    },
    approveOffer: {
      method: 'POST',
      path: '/offers/:offerId/approve',
      body: decideOfferBodySchema,
      responses: {
        200: offerLetterSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Approve the step currently awaiting the caller',
      metadata: routeMeta(null, { salaryPin: 'others' }),
    },
    rejectOffer: {
      method: 'POST',
      path: '/offers/:offerId/reject',
      body: decideOfferBodySchema,
      responses: {
        200: offerLetterSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Reject the step currently awaiting the caller',
      metadata: routeMeta(null, { salaryPin: 'others' }),
    },
    sendOffer: {
      method: 'POST',
      path: '/offers/:offerId/send',
      headers: z.object({ 'idempotency-key': idempotencyKeySchema }),
      body: sendOfferBodySchema,
      responses: {
        200: offerLetterSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Email the approved offer to the candidate with their acceptance link',
      metadata: routeMeta(['offer:create', 'offer:send'], { salaryPin: 'others' }),
    },
    resendOffer: {
      method: 'POST',
      path: '/offers/:offerId/resend',
      headers: z.object({ 'idempotency-key': idempotencyKeySchema }),
      body: sendOfferBodySchema,
      responses: {
        200: offerLetterSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        'Email a sent or expired offer again with a fresh link and expiry; the previous link stops working',
      metadata: routeMeta(['offer:create', 'offer:send'], { salaryPin: 'others' }),
    },
    reviseOffer: {
      method: 'POST',
      path: '/offers/:offerId/revise',
      headers: z.object({ 'idempotency-key': idempotencyKeySchema }),
      body: reviseOfferBodySchema,
      responses: {
        201: offerLetterSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Replace an offer with a new version; a new salary or designation goes back through approval',
      metadata: routeMeta(['offer:create', 'offer.compensation:read'], { salaryPin: 'others' }),
    },
    downloadSignedOffer: {
      method: 'GET',
      path: '/offers/:offerId/signed',
      responses: { 200: recruitmentFileSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      summary: "The signed offer as a document: the letter, the candidate's e-signature and its evidence",
      metadata: routeMeta(['offer:create', 'offer.compensation:read'], { ai: 'hidden', salaryPin: 'others' }),
    },
    listOfferCtcTemplates: {
      method: 'GET',
      path: '/offer-ctc-templates',
      responses: { 200: z.array(offerCtcTemplateSchema), 401: errorBodySchema, 403: errorBodySchema },
      summary: 'Salary structures an offer can be built on',
      metadata: routeMeta(['offer:create', 'offer.compensation:read']),
    },
    listOfferSignatories: {
      method: 'GET',
      path: '/offer-signatories',
      responses: { 200: z.array(offerSignatorySchema), 401: errorBodySchema, 403: errorBodySchema },
      summary: 'The company signatories whose name and signature an offer letter can carry',
      metadata: routeMeta('offer:create'),
    },
    createOfferSignatory: {
      method: 'POST',
      path: '/offer-signatories',
      body: createOfferSignatoryBodySchema,
      responses: { 201: offerSignatorySchema, 401: errorBodySchema, 403: errorBodySchema, 422: errorBodySchema },
      summary: 'Add an authorised signatory for offer letters',
      metadata: routeMeta('offer:send'),
    },
    updateOfferSignatory: {
      method: 'PATCH',
      path: '/offer-signatories/:signatoryId',
      body: updateOfferSignatoryBodySchema,
      responses: {
        200: offerSignatorySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Edit or switch off an offer signatory',
      metadata: routeMeta('offer:send'),
    },
    listInternalJobs: {
      method: 'GET',
      path: '/internal-jobs',
      query: listInternalJobsQuerySchema,
      responses: { 200: paginatedSchema(internalJobSchema), 401: errorBodySchema },
      summary: 'Open roles current employees can apply to',
      metadata: routeMeta(null),
    },
    applyToInternalJob: {
      method: 'POST',
      path: '/internal-jobs/:postingId/apply',
      body: applyToInternalJobBodySchema,
      responses: {
        201: z.object({ received: z.literal(true), applicationRef: z.string() }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: "Apply to an internal posting with the employee's own profile; their manager is told",
      metadata: routeMeta(null),
    },
    rescindOffer: {
      method: 'POST',
      path: '/offers/:offerId/rescind',
      headers: z.object({ 'idempotency-key': idempotencyKeySchema }),
      body: rescindOfferBodySchema,
      responses: {
        200: offerLetterSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Rescind a sent-but-not-yet-accepted offer',
      metadata: routeMeta(['offer:create', 'offer:rescind'], { salaryPin: 'others' }),
    },

    // ---- Ranking a posting's candidates ------------------------------------
    rescorePostingCandidates: {
      method: 'POST',
      path: '/postings/:postingId/rescore',
      body: z.object({}),
      responses: {
        200: z.object({ updated: z.number().int().nonnegative() }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "Work out every candidate's free skills-and-years match against the posting again",
      metadata: routeMeta('candidate:manage', { ai: 'hidden' }),
    },
    scoreTopCandidates: {
      method: 'POST',
      path: '/postings/:postingId/ai-score-top',
      body: scoreTopBodySchema,
      responses: {
        200: scoreTopResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Run the AI fit estimate on the best-matching candidates that do not have one yet',
      metadata: routeMeta('candidate:manage', { ai: 'hidden' }),
    },

    // ---- Candidate import ------------------------------------------------
    parseCandidateSpreadsheet: {
      method: 'POST',
      path: '/candidate-import/parse',
      body: parseCandidateSpreadsheetBodySchema,
      responses: {
        200: parsedSpreadsheetSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Read an Excel file into rows so the columns can be matched',
      metadata: routeMeta('candidate:manage', { ai: 'hidden' }),
    },
    previewCandidateImport: {
      method: 'POST',
      path: '/postings/:postingId/import/preview',
      body: previewCandidateImportBodySchema,
      responses: {
        200: candidateImportPreviewSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Show what importing these rows would do, row by row, without saving anything',
      metadata: routeMeta('candidate:manage', { ai: 'hidden' }),
    },
    importCandidates: {
      method: 'POST',
      path: '/postings/:postingId/import',
      headers: z.object({ 'idempotency-key': idempotencyKeySchema }),
      body: importCandidatesBodySchema,
      responses: {
        201: candidateImportResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Add candidates from a job-board export or spreadsheet to a posting',
      metadata: routeMeta('candidate:manage', { ai: 'hidden' }),
    },

    // ---- Profiles, checks, joining, agencies, saved searches (R7) --------
    listCandidateApplications: {
      method: 'GET',
      path: '/candidates/:candidateId/applications',
      responses: { 200: z.array(candidateApplicationSchema), 401: errorBodySchema, 404: errorBodySchema },
      summary: "Other applications by the same person, in postings the caller may see",
      metadata: routeMeta('candidate:read'),
    },
    linkCandidate: {
      method: 'POST',
      path: '/candidates/:candidateId/link',
      body: linkCandidateBodySchema,
      responses: { 200: z.array(candidateApplicationSchema), 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema, 409: errorBodySchema },
      summary: 'Say two applications are the same person, so they share one profile',
      metadata: routeMeta(['candidate:manage', 'candidate:read']),
    },
    unlinkCandidate: {
      method: 'POST',
      path: '/candidates/:candidateId/unlink',
      body: z.object({}),
      responses: { 200: z.array(candidateApplicationSchema), 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      summary: 'Take an application out of a shared profile',
      metadata: routeMeta(['candidate:manage', 'candidate:read']),
    },
    listCandidateChecks: {
      method: 'GET',
      path: '/candidates/:candidateId/checks',
      responses: { 200: z.array(candidateCheckSchema), 401: errorBodySchema, 404: errorBodySchema },
      summary: "A candidate's background checks",
      metadata: routeMeta(['candidate:manage', 'candidate:read']),
    },
    createCandidateCheck: {
      method: 'POST',
      path: '/candidates/:candidateId/checks',
      body: createCandidateCheckBodySchema,
      responses: { 201: candidateCheckSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema, 409: errorBodySchema },
      summary: 'Start a background check on a candidate',
      metadata: routeMeta(['candidate:manage', 'candidate:read']),
    },
    updateCandidateCheck: {
      method: 'PATCH',
      path: '/candidates/:candidateId/checks/:checkId',
      body: updateCandidateCheckBodySchema,
      responses: { 200: candidateCheckSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      summary: "Record a background check's progress or result",
      metadata: routeMeta(['candidate:manage', 'candidate:read']),
    },
    deleteCandidateCheck: {
      method: 'DELETE',
      path: '/candidates/:candidateId/checks/:checkId',
      responses: { 200: z.object({ deleted: z.literal(true) }), 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      summary: 'Remove a background check that was added by mistake',
      metadata: routeMeta(['candidate:manage', 'candidate:read']),
    },
    downloadInterviewCalendar: {
      method: 'GET',
      path: '/interviews/:interviewId/calendar',
      responses: { 200: recruitmentFileSchema, 401: errorBodySchema, 404: errorBodySchema },
      summary: 'An interview as a calendar file (.ics) for anyone on its panel',
      metadata: routeMeta(null, { ai: 'hidden' }),
    },
    listJoiningTracker: {
      method: 'GET',
      path: '/joining',
      responses: { 200: z.array(joiningEntrySchema), 401: errorBodySchema, 403: errorBodySchema },
      summary: 'Accepted offers between acceptance and the first day, with background-check status',
      metadata: routeMeta('offer:create'),
    },
    markOfferDidNotJoin: {
      method: 'POST',
      path: '/offers/:offerId/did-not-join',
      body: didNotJoinBodySchema,
      responses: {
        200: offerLetterSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'The candidate accepted but never joined: close the pre-joining record and reopen the seat',
      metadata: routeMeta('offer:create', { salaryPin: 'others' }),
    },
    listAgencies: {
      method: 'GET',
      path: '/agencies',
      responses: { 200: z.array(recruitmentAgencySchema), 401: errorBodySchema, 403: errorBodySchema },
      summary: 'Recruitment agencies that can submit candidates through their own link',
      metadata: routeMeta('requisition:update'),
    },
    createAgency: {
      method: 'POST',
      path: '/agencies',
      body: createAgencyBodySchema,
      responses: { 201: agencyWithTokenSchema, 401: errorBodySchema, 403: errorBodySchema, 422: errorBodySchema },
      summary: "Add an agency; the portal link's token is returned once",
      metadata: routeMeta('requisition:update', { ai: 'hidden' }),
    },
    updateAgency: {
      method: 'PATCH',
      path: '/agencies/:agencyId',
      body: updateAgencyBodySchema,
      responses: { 200: recruitmentAgencySchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      summary: "Change an agency's details, fee or whether it may submit",
      metadata: routeMeta('requisition:update'),
    },
    rotateAgencyToken: {
      method: 'POST',
      path: '/agencies/:agencyId/rotate-token',
      body: z.object({}),
      responses: { 200: agencyWithTokenSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      summary: "Replace an agency's portal link; the old one stops working",
      metadata: routeMeta('requisition:update', { ai: 'hidden' }),
    },
    listTalentPoolSearches: {
      method: 'GET',
      path: '/talent-pool-searches',
      responses: { 200: z.array(talentPoolSearchSchema), 401: errorBodySchema },
      summary: 'Saved talent-pool searches',
      metadata: routeMeta('candidate:read'),
    },
    createTalentPoolSearch: {
      method: 'POST',
      path: '/talent-pool-searches',
      body: createTalentPoolSearchBodySchema,
      responses: { 201: talentPoolSearchSchema, 401: errorBodySchema, 403: errorBodySchema, 422: errorBodySchema },
      summary: 'Save a talent-pool search to come back to',
      metadata: routeMeta('candidate:manage'),
    },
    deleteTalentPoolSearch: {
      method: 'DELETE',
      path: '/talent-pool-searches/:searchId',
      responses: { 200: z.object({ deleted: z.literal(true) }), 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      summary: 'Delete a saved talent-pool search',
      metadata: routeMeta('candidate:manage'),
    },

    // ---- Screening questions, careers settings, AI, erasure (R6) ---------
    listPostingQuestions: {
      method: 'GET',
      path: '/postings/:postingId/questions',
      responses: { 200: z.array(postingQuestionSchema), 401: errorBodySchema, 404: errorBodySchema },
      summary: "The questions on a posting's apply form, with their knockout rules",
      metadata: routeMeta('requisition:read'),
    },
    setPostingQuestions: {
      method: 'PUT',
      path: '/postings/:postingId/questions',
      body: setPostingQuestionsBodySchema,
      responses: {
        200: z.array(postingQuestionSchema),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: "Replace a posting's apply-form questions",
      metadata: routeMeta(['requisition:update', 'requisition:read']),
    },
    listCandidateAnswers: {
      method: 'GET',
      path: '/candidates/:candidateId/answers',
      responses: { 200: z.array(candidateAnswerSchema), 401: errorBodySchema, 404: errorBodySchema },
      summary: "A candidate's answers to the apply-form questions",
      metadata: routeMeta('candidate:read'),
    },
    getCareersSettings: {
      method: 'GET',
      path: '/careers-settings',
      responses: { 200: careersSettingsSchema, 401: errorBodySchema, 403: errorBodySchema },
      summary: 'Branding, privacy notice and retention period of the careers pages',
      metadata: routeMeta('requisition:update'),
    },
    updateCareersSettings: {
      method: 'PATCH',
      path: '/careers-settings',
      body: updateCareersSettingsBodySchema,
      responses: {
        200: careersSettingsSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Change the careers pages branding, privacy notice or retention period',
      metadata: routeMeta('requisition:update'),
    },
    generateJobDescription: {
      method: 'POST',
      path: '/ai/job-description',
      body: generateJobDescriptionBodySchema,
      responses: {
        200: generatedJobDescriptionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
        503: errorBodySchema,
      },
      summary: 'Draft a job description and requirements from a title, level and skills',
      metadata: routeMeta('requisition:update', { ai: 'hidden' }),
    },
    scoreCandidate: {
      method: 'POST',
      path: '/candidates/:candidateId/ai-score',
      body: z.object({}),
      responses: {
        200: candidateMatchSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        503: errorBodySchema,
      },
      summary: "Estimate how well a candidate fits the posting, with reasons (advice only)",
      metadata: routeMeta(['candidate:manage', 'candidate:read'], { ai: 'hidden' }),
    },
    summariseCandidateScorecards: {
      method: 'POST',
      path: '/candidates/:candidateId/scorecard-summary',
      body: z.object({}),
      responses: {
        200: scorecardTextSummarySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        503: errorBodySchema,
      },
      summary: 'A short written summary of the interview scorecards this person may see',
      metadata: routeMeta(null, { ai: 'hidden' }),
    },
    eraseCandidate: {
      method: 'POST',
      path: '/candidates/:candidateId/erase',
      body: eraseCandidateBodySchema,
      responses: {
        200: z.object({ erased: z.literal(true) }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Permanently delete a candidate\'s personal data (a data-deletion request)',
      metadata: routeMeta(['candidate:manage', 'candidate:read'], { ai: 'hidden' }),
    },

    // ---- Analytics ---------------------------------------------------------
    getAnalytics: {
      method: 'GET',
      path: '/analytics',
      query: recruitmentAnalyticsQuerySchema,
      responses: { 200: recruitmentAnalyticsSchema, 401: errorBodySchema, 403: errorBodySchema },
      summary: 'Recruiting funnel, source, and velocity metrics',
      metadata: routeMeta('recruitment.analytics:read'),
    },

    // ---- Offer templates ---------------------------------------------------
    listOfferTemplates: {
      method: 'GET',
      path: '/offer-templates',
      query: listOfferTemplatesQuerySchema,
      responses: { 200: paginatedSchema(offerTemplateSchema), 401: errorBodySchema },
      summary: 'List offer-letter templates',
      metadata: routeMeta(null),
    },
    getOfferTemplate: {
      method: 'GET',
      path: '/offer-templates/:templateId',
      responses: { 200: offerTemplateSchema, 401: errorBodySchema, 404: errorBodySchema },
      summary: 'Get one offer-letter template',
      metadata: routeMeta('offer:create'),
    },
    createOfferTemplate: {
      method: 'POST',
      path: '/offer-templates',
      body: createOfferTemplateBodySchema,
      responses: {
        201: offerTemplateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Create an offer-letter template',
      metadata: routeMeta('offer:create'),
    },
    updateOfferTemplate: {
      method: 'PATCH',
      path: '/offer-templates/:templateId',
      body: updateOfferTemplateBodySchema,
      responses: {
        200: offerTemplateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Edit an offer-letter template',
      metadata: routeMeta('offer:create'),
    },

    // ---- Interview slot offers ("pick a slot") -----------------------------
    listSlotOffers: {
      method: 'GET',
      path: '/slot-offers',
      query: listSlotOffersQuerySchema,
      responses: { 200: paginatedSchema(interviewSlotOfferSchema), 401: errorBodySchema },
      summary: 'List "pick a slot" links raised for candidates',
      metadata: routeMeta(null),
    },
    createSlotOffer: {
      method: 'POST',
      path: '/slot-offers',
      body: createSlotOfferBodySchema,
      responses: {
        201: interviewSlotOfferSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Propose candidate-facing interview time slots and get a shareable link',
      metadata: routeMeta(['candidate:read', 'interview:schedule']),
    },
    cancelSlotOffer: {
      method: 'POST',
      path: '/slot-offers/:slotOfferId/cancel',
      body: c.noBody(),
      responses: {
        200: interviewSlotOfferSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Withdraw an open "pick a slot" link',
      metadata: routeMeta(['interview:read', 'interview:schedule']),
    },

    // ---- Referral bonus -----------------------------------------------------
    setReferralBonus: {
      method: 'POST',
      path: '/candidates/:candidateId/referral-bonus',
      body: setReferralBonusBodySchema,
      responses: {
        200: candidateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Record the intended referral bonus for a candidate hired via employee referral',
      metadata: routeMeta(['candidate:manage', 'candidate:read']),
    },
    updateReferralBonusStatus: {
      method: 'POST',
      path: '/candidates/:candidateId/referral-bonus/status',
      body: updateReferralBonusStatusBodySchema,
      responses: {
        200: candidateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        'Mark an approved referral bonus paid. Refused for whoever submitted it for approval',
      metadata: routeMeta(['candidate:manage', 'candidate:read']),
    },
    submitReferralBonus: {
      method: 'POST',
      path: '/candidates/:candidateId/referral-bonus/submit',
      body: c.noBody(),
      responses: {
        200: candidateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Send a pending referral bonus for approval',
      metadata: routeMeta(['candidate:manage', 'candidate:read']),
    },
    approveReferralBonus: {
      method: 'POST',
      path: '/candidates/:candidateId/referral-bonus/approve',
      body: decideReferralBonusBodySchema,
      responses: {
        200: candidateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Approve the referral-bonus step currently awaiting the caller',
      metadata: routeMeta(null),
    },
    rejectReferralBonus: {
      method: 'POST',
      path: '/candidates/:candidateId/referral-bonus/reject',
      body: decideReferralBonusBodySchema,
      responses: {
        200: candidateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Reject the referral-bonus step currently awaiting the caller',
      metadata: routeMeta(null),
    },

    // ---- Assessments ----------------------------------------------------------
    listAssessmentTemplates: {
      method: 'GET',
      path: '/assessment-templates',
      query: listAssessmentTemplatesQuerySchema,
      responses: { 200: paginatedSchema(assessmentTemplateSchema), 401: errorBodySchema },
      summary: 'List assessment question-set templates',
      metadata: routeMeta(null),
    },
    getAssessmentTemplate: {
      method: 'GET',
      path: '/assessment-templates/:templateId',
      responses: { 200: assessmentTemplateSchema, 401: errorBodySchema, 404: errorBodySchema },
      summary: 'Get one assessment template',
      metadata: routeMeta('candidate:read'),
    },
    createAssessmentTemplate: {
      method: 'POST',
      path: '/assessment-templates',
      body: createAssessmentTemplateBodySchema,
      responses: {
        201: assessmentTemplateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Create an assessment template with its questions',
      metadata: routeMeta('candidate:manage'),
    },
    updateAssessmentTemplate: {
      method: 'PATCH',
      path: '/assessment-templates/:templateId',
      body: updateAssessmentTemplateBodySchema,
      responses: {
        200: assessmentTemplateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Edit an assessment template',
      metadata: routeMeta('candidate:manage'),
    },
    listCandidateAssessments: {
      method: 'GET',
      path: '/candidate-assessments',
      query: listCandidateAssessmentsQuerySchema,
      responses: { 200: paginatedSchema(candidateAssessmentSchema), 401: errorBodySchema },
      summary: 'List assessments sent to candidates',
      metadata: routeMeta(null),
    },
    sendAssessment: {
      method: 'POST',
      path: '/candidate-assessments',
      body: sendAssessmentBodySchema,
      responses: {
        201: candidateAssessmentSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Send a template to a candidate — emails them the link to answer it',
      metadata: routeMeta(['candidate:manage', 'candidate:read']),
    },

    // ---- Talent pool -----------------------------------------------------------
    listTalentPool: {
      method: 'GET',
      path: '/talent-pool',
      query: listTalentPoolQuerySchema,
      responses: { 200: paginatedSchema(talentPoolEntrySchema), 401: errorBodySchema },
      summary: 'List candidates kept for future roles',
      metadata: routeMeta(null),
    },
    addToTalentPool: {
      method: 'POST',
      path: '/talent-pool',
      body: addToTalentPoolBodySchema,
      responses: {
        201: talentPoolEntrySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Add a candidate to the talent pool',
      metadata: routeMeta(['candidate:manage', 'candidate:read']),
    },
    removeFromTalentPool: {
      method: 'POST',
      path: '/talent-pool/:entryId/remove',
      body: c.noBody(),
      responses: {
        200: talentPoolEntrySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Remove a candidate from the talent pool',
      metadata: routeMeta(['candidate:manage', 'candidate:read']),
    },
  },
  { pathPrefix: '/recruitment' },
);
